import React, { useState, useEffect, useMemo } from 'react';
import {
  Search,
  Plus,
  FileSpreadsheet,
  Package,
  Truck,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Trash2,
  Sparkles,
  Layers,
  X,
  Loader2,
  ShieldCheck,
  Building2,
  FolderKanban,
  ClipboardList,
  Edit3,
  Scissors
} from 'lucide-react';
import {
  Order360Detail,
  OrderLine360,
  Orders360CatalogResponse,
  PTFurnitureItem,
  BOMMaterialRef,
  OverrideBomLine,
  OrderLineType,
  LogisticsRouteType,
  PTCatalogOverviewResponse,
  CRMProject,
  CRMClient,
  CRMBranch,
  ORDER_DOC_TYPES,
  ORDER_STATUSES,
  ORDER_LINE_STATUSES,
  ORDER_DISPATCH_STATUSES,
  ORDER_LINE_TYPES,
  LOGISTICS_ROUTE_TYPES
} from '../types/crm';

interface OrdersAndIngestion360TabProps {
  token?: string | null;
  projects: CRMProject[];
  clients: CRMClient[];
  branches: CRMBranch[];
  initialSearchTerm?: string;
  onOpenStoreAllocations?: (projectName?: string) => void;
  onOpenBOMAuditForOrder?: (order: { id: string; codigo: string; proyecto: string }) => void;
  onOpenMRPExplosion?: (order: { id: string; codigo: string }) => void;
  onOrderSaved?: () => void;
  onNotify?: (type: 'success' | 'error', text: string) => void;
}

// Abreviador industrial en cliente para protección térmica 4-UP (<= 80 caracteres)
function clientAbbreviate4UP(rawText: string, maxLen = 80): string {
  let s = String(rawText || '')
    .replace(/\uFFFD/g, 'Ñ')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();
  if (!s) return 'COMPONENTE MOBILIARIO';
  if (s.length <= maxLen) return s;

  const rules: [RegExp, string][] = [
    [/\bCON\s+/g, 'C/'],
    [/\bPARA\s+/g, 'P/'],
    [/\bSIN\s+/g, 'S/'],
    [/\bINCLUYE\s+/g, 'INC. '],
    [/\bESTRUCTURA\s+DE\s+/g, 'EST. '],
    [/\bESTRUCTURA\s+/g, 'EST. '],
    [/\bEXHIBIDOR\s+DE\s+/g, 'EXH. '],
    [/\bEXHIBIDOR\s+/g, 'EXH. '],
    [/\bMOSTRADOR\s+/g, 'MOST. '],
    [/\bENTREPAÑOS\s+INTERNOS\b/g, 'ENTREP. INT.'],
    [/\bENTREPAÑOS\b/g, 'ENTREP.'],
    [/\bINOXIDABLE\b/g, 'INOX'],
    [/\bGALVANIZADO\b/g, 'GALV'],
    [/\bILUMINACION\b/g, 'ILUM.'],
    [/\bCENTIMETROS\b/g, 'CM'],
    [/\bMILIMETROS\b/g, 'MM'],
    [/\bUNIDADES\b/g, 'UND'],
    [/\bIZQUIERDO\b/g, 'IZQ'],
    [/\bIZQUIERDA\b/g, 'IZQ'],
    [/\bDERECHO\b/g, 'DER'],
    [/\bDERECHA\b/g, 'DER']
  ];

  for (const [rx, rep] of rules) {
    s = s.replace(rx, rep).replace(/\s+/g, ' ').trim();
    if (s.length <= maxLen) return s;
  }
  return s.slice(0, maxLen - 3).trim() + '...';
}

// PLAN-059 (P59-D1): Cortafuegos Automático 4-UP en Cliente para Rampa QR (pedidos_activos.js)
function buildClientRampaActivosPayload(ord: Order360Detail) {
  const lines = Array.isArray(ord?.lines) ? ord.lines : [];
  const exportedItems: any[] = [];
  let excludedServicesCount = 0;
  let excludedForeignCount = 0;
  let consolidatedParametricCount = 0;

  for (const rawLine of lines) {
    const tipoLinea = rawLine.tipoLinea || 'PT_FABRICADO';
    const uom = String(rawLine.unidadMedida || 'UND').trim().toUpperCase();
    const isContinuous =
      tipoLinea === 'PT_PARAMETRICO' || ['ML', 'METROS', 'MT2', 'M2', 'KG', 'LT'].includes(uom);

    if (tipoLinea === 'SERVICIO_INTANGIBLE' || String(rawLine.code || '').toUpperCase().startsWith('SERV-')) {
      excludedServicesCount++;
      continue;
    }

    if (
      (tipoLinea === 'SUMINISTRO_FORANEO' && rawLine.rutaLogistica !== 'VIA_GALPON') ||
      rawLine.requiereEmpaqueGalpon === false ||
      rawLine.bultosCalculados4UP === 0
    ) {
      excludedForeignCount++;
      continue;
    }

    let descEtiqueta = String(rawLine.descEtiqueta || rawLine.desc || rawLine.code || 'COMPONENTE').trim();
    let cantidadTotal = Math.max(1, Math.round(Number(rawLine.cantidad) || 1));
    let empaqueNominal = Math.max(1, Math.round(Number(rawLine.empaqueNominal) || 1));

    if (isContinuous) {
      consolidatedParametricCount++;
      const bultos =
        Number(rawLine.bultosDeclarados) > 0
          ? Math.round(Number(rawLine.bultosDeclarados))
          : Number(rawLine.bultosCalculados4UP) > 0
            ? Math.round(Number(rawLine.bultosCalculados4UP))
            : 1;
      cantidadTotal = Math.max(1, bultos);
      empaqueNominal = 1;
      const displayUom = uom === 'METROS' ? 'ML' : uom === 'M2' ? 'MT2' : uom;
      const measureTag = `[${Number(rawLine.cantidad || 1)} ${displayUom}]`;
      if (!descEtiqueta.toUpperCase().includes(displayUom)) {
        descEtiqueta = clientAbbreviate4UP(`${measureTag} ${descEtiqueta}`, 80);
      } else {
        descEtiqueta = clientAbbreviate4UP(descEtiqueta, 80);
      }
    } else {
      descEtiqueta = clientAbbreviate4UP(descEtiqueta, 80);
    }

    exportedItems.push({
      code: rawLine.code,
      code_etiqueta: rawLine.codeEtiqueta || rawLine.code,
      desc: rawLine.desc || descEtiqueta,
      desc_etiqueta: descEtiqueta,
      cantidad_total: cantidadTotal,
      color: String(rawLine.color || 'ESTÁNDAR').trim().toUpperCase(),
      tipo: rawLine.familia || 'Mobiliario Comercial',
      empaque_nominal_sugerido: empaqueNominal
    });
  }

  return {
    payload: {
      order_id: ord.codigo,
      order_number: ord.correlativoValery || ord.codigo,
      title: `${ord.codigo} — ${ord.clienteNombre} (${ord.sucursalNombre})`,
      cliente: ord.clienteNombre,
      ubicacion: ord.ciudadEstado || 'VENEZUELA',
      sucursal: ord.sucursalNombre,
      items: exportedItems
    },
    stats: {
      exportedCount: exportedItems.length,
      excludedServicesCount,
      excludedForeignCount,
      consolidatedParametricCount
    }
  };
}

export const OrdersAndIngestion360Tab: React.FC<OrdersAndIngestion360TabProps> = ({
  token,
  projects,
  clients,
  branches,
  initialSearchTerm,
  onOpenStoreAllocations,
  onOpenBOMAuditForOrder,
  onOpenMRPExplosion,
  onOrderSaved,
  onNotify
}) => {
  const [data, setData] = useState<Orders360CatalogResponse | null>(null);
  const [ptCatalog, setPtCatalog] = useState<PTFurnitureItem[]>([]);
  const [materialsCatalog, setMaterialsCatalog] = useState<BOMMaterialRef[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Filtros reactivos
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [modeFilter, setModeFilter] = useState<'ALL' | 'WITH_LINES' | 'RAMPA' | 'PRODUCTION' | 'PRS'>(
    initialSearchTerm ? 'ALL' : 'WITH_LINES'
  );
  const [visibleLimit, setVisibleLimit] = useState<number>(30);
  const [showRampaJsonExport, setShowRampaJsonExport] = useState<boolean>(false);

  useEffect(() => {
    if (initialSearchTerm !== undefined && initialSearchTerm.trim() !== '') {
      setSearchTerm(initialSearchTerm);
      setModeFilter('ALL');
    }
  }, [initialSearchTerm]);

  // Modal 1: Inspector 360° de un Pedido y sus Renglones 4-UP
  const [inspectModal, setInspectModal] = useState<{
    open: boolean;
    order: Order360Detail | null;
  }>({ open: false, order: null });

  // Modal 2: Constructor Manual Asistido e Importador Híbrido Masivo 360°
  const [builderModal, setBuilderModal] = useState<{
    open: boolean;
    initialTab: 'manual' | 'bulk_ingest';
    editingOrderId?: string | null;
  }>({ open: false, initialTab: 'manual', editingOrderId: null });

  const [activeBuilderTab, setActiveBuilderTab] = useState<'manual' | 'bulk_ingest'>('manual');

  // Formulario de Cabecera del Pedido (Odoo 18 sale.order)
  const [orderForm, setOrderForm] = useState({
    id: '' as string,
    codigo: '',
    correlativoValery: '',
    tipoDocumento: 'Pedido (PED)',
    estado: 'Confirmado',
    estadoAuditoriaBOM: 'Sin Auditar',
    moneda: 'USD',
    fechaDocumento: new Date().toISOString().slice(0, 10),
    fechaEntregaEstimada: '',
    proyectoId: '',
    clienteId: '',
    sucursalId: '',
    direccionEntrega: '',
    contactoEntrega: '',
    notas: '',
    montoTotalUSD: 0
  });

  // Estado editable de Renglones 4-UP del Pedido
  const [editableLines, setEditableLines] = useState<OrderLine360[]>([]);

  // Controles de adición manual asistida desde el Catálogo PT (1.282 muebles + Polimorfismo PLAN-059)
  const [ptSearch, setPtSearch] = useState('');
  const [selectedPtCode, setSelectedPtCode] = useState('');
  const [newLineQty, setNewLineQty] = useState('1');
  const [newLineColor, setNewLineColor] = useState('NEGRO');
  const [newLineVariantTag, setNewLineVariantTag] = useState('');
  const [newLinePriceUSD, setNewLinePriceUSD] = useState('');
  const [newLineUnit, setNewLineUnit] = useState('UND');
  const [newLineType, setNewLineType] = useState<OrderLineType>('PT_FABRICADO');
  const [newLineBultos, setNewLineBultos] = useState('');
  const [autoDecomposeKit, setAutoDecomposeKit] = useState(true);

  // Modal 3 (PLAN-059 P59-D3): Mini-Editor Asistido de Receta Override BOM por Renglón
  const [overrideBomModal, setOverrideBomModal] = useState<{
    open: boolean;
    lineIdx: number | null;
    lines: OverrideBomLine[];
    matSearch: string;
    selectedMatCode: string;
    qtyObra: string;
    mermaPct: string;
    notas: string;
  }>({
    open: false,
    lineIdx: null,
    lines: [],
    matSearch: '',
    selectedMatCode: '',
    qtyObra: '1',
    mermaPct: '0',
    notas: ''
  });

  // Controles de Ingesta Masiva 360° (TSV/CSV/JSON + Conciliación Presupuesto vs Proyecto)
  const [bulkRawText, setBulkRawText] = useState('');
  const [budgetExpectedUnits, setBudgetExpectedUnits] = useState<string>('');
  const [supervisorPIN, setSupervisorPIN] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const notify = (type: 'success' | 'error', text: string) => {
    if (onNotify) onNotify(type, text);
  };

  const getAuthHeaders = () => {
    const h: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) h['Authorization'] = `Bearer ${token}`;
    return h;
  };

  const fetchOrdersCatalog = async (force = false) => {
    setLoading(true);
    setErrorMsg(null);
    try {
      const [ordRes, ptRes] = await Promise.all([
        fetch(`/api/orders/catalog${force ? '?force=1' : ''}`),
        ptCatalog.length === 0 ? fetch('/api/bom/catalog') : Promise.resolve(null)
      ]);
      const ordJson: Orders360CatalogResponse = await ordRes.json();
      if (!ordRes.ok || ordJson.error) {
        throw new Error(ordJson.error || `Error HTTP ${ordRes.status}`);
      }
      setData(ordJson);

      if (ptRes && ptRes.ok) {
        const ptJson: PTCatalogOverviewResponse = await ptRes.json();
        if (Array.isArray(ptJson.items)) {
          setPtCatalog(ptJson.items);
        }
        if (Array.isArray(ptJson.materialsCatalog)) {
          setMaterialsCatalog(ptJson.materialsCatalog);
        }
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Error cargando catálogo 360° de Pedidos y Líneas 4-UP.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrdersCatalog(false);
  }, []);

  useEffect(() => {
    setVisibleLimit(30);
  }, [searchTerm, modeFilter]);

  const orders = data?.orders || [];

  const ptByCode = useMemo(() => {
    const map = new Map<string, PTFurnitureItem>();
    for (const pt of ptCatalog) {
      const c = pt.codigo.toUpperCase();
      map.set(c, pt);
      map.set(c.replace(/\*+$/, ''), pt);
    }
    return map;
  }, [ptCatalog]);

  const filteredPtCatalog = useMemo(() => {
    const q = ptSearch.trim().toLowerCase();
    if (!q) return ptCatalog;
    return ptCatalog.filter(
      pt => pt.codigo.toLowerCase().includes(q) || pt.nombre.toLowerCase().includes(q)
    );
  }, [ptCatalog, ptSearch]);

  const filteredOrders = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    return orders.filter(ord => {
      if (modeFilter === 'WITH_LINES' && ord.linesCount === 0) return false;
      if (modeFilter === 'RAMPA' && !ord.inRampaActivos) return false;
      if (
        modeFilter === 'PRODUCTION' &&
        ord.estado !== 'Confirmado' &&
        ord.estado !== 'En producción'
      ) {
        return false;
      }
      if (modeFilter === 'PRS' && ord.tipoCorto !== 'PRS') return false;

      if (!q) return true;
      return (
        ord.codigo.toLowerCase().includes(q) ||
        ord.clienteNombre.toLowerCase().includes(q) ||
        ord.proyectoNombre.toLowerCase().includes(q) ||
        ord.sucursalNombre.toLowerCase().includes(q) ||
        ord.lines.some(
          l =>
            l.code.toLowerCase().includes(q) ||
            l.codeEtiqueta.toLowerCase().includes(q) ||
            l.descEtiqueta.toLowerCase().includes(q)
        )
      );
    });
  }, [orders, modeFilter, searchTerm]);

  // Al seleccionar Proyecto en el formulario de Pedido, autocompletar Cliente, Sucursal y Dirección
  const handleSelectProjectForOrder = (projId: string) => {
    const proj = projects.find(p => p.id === projId);
    if (!proj) {
      setOrderForm(prev => ({ ...prev, proyectoId: projId }));
      return;
    }
    const branch = proj.sucursalId ? branches.find(b => b.id === proj.sucursalId) : null;
    setOrderForm(prev => ({
      ...prev,
      proyectoId: proj.id,
      clienteId: proj.clienteId || prev.clienteId,
      sucursalId: proj.sucursalId || prev.sucursalId,
      direccionEntrega: branch?.direccion || branch?.ciudadEstado || prev.direccionEntrega,
      contactoEntrega: branch?.contactoTienda || prev.contactoEntrega
    }));
  };

  // Abrir Constructor en modo Nuevo Pedido Manual o Ingesta Masiva
  const openCreateOrderModal = (initialTab: 'manual' | 'bulk_ingest') => {
    const defaultProj = projects[0];
    setOrderForm({
      id: '',
      codigo: `PED-${new Date().getFullYear()}-${String(orders.length + 1).padStart(3, '0')}`,
      correlativoValery: '',
      tipoDocumento: 'Pedido (PED)',
      estado: 'Confirmado',
      estadoAuditoriaBOM: 'Sin Auditar',
      moneda: 'USD',
      fechaDocumento: new Date().toISOString().slice(0, 10),
      fechaEntregaEstimada: '',
      proyectoId: defaultProj?.id || '',
      clienteId: defaultProj?.clienteId || '',
      sucursalId: defaultProj?.sucursalId || '',
      direccionEntrega: defaultProj?.ciudadEstado || '',
      contactoEntrega: '',
      notas: '',
      montoTotalUSD: 0
    });
    setEditableLines([]);
    setBulkRawText(
      `P484\tMODULO DE CAJA 76X75X70 CON AUXILIAR ADIDAS 96X75X70CM\t1\tBLANCO\nP1257\tEXHIBIDOR DE CALZADO 230CM DOBLE TUBO PISO PARED CON 9 BASES FIJAS\t12\tNEGRO`
    );
    setBudgetExpectedUnits('13');
    setActiveBuilderTab(initialTab);
    setBuilderModal({ open: true, initialTab, editingOrderId: null });
  };

  // Abrir Editor sobre un Pedido existente
  const openEditOrderModal = (ord: Order360Detail) => {
    setOrderForm({
      id: ord.id,
      codigo: ord.codigo,
      correlativoValery: ord.correlativoValery || ord.codigo,
      tipoDocumento: ord.tipoDocumento || 'Pedido (PED)',
      estado: ord.estado || 'Confirmado',
      estadoAuditoriaBOM: ord.estadoAuditoriaBOM || 'Sin Auditar',
      moneda: ord.moneda || 'USD',
      fechaDocumento: ord.fechaDocumento || new Date().toISOString().slice(0, 10),
      fechaEntregaEstimada: ord.fechaEntregaEstimada || '',
      proyectoId: ord.proyectoId || '',
      clienteId: ord.clienteId || '',
      sucursalId: ord.sucursalId || '',
      direccionEntrega: ord.direccionEntrega || '',
      contactoEntrega: ord.contactoEntrega || '',
      notas: ord.notas || '',
      montoTotalUSD: ord.montoTotalUSD || 0
    });
    setEditableLines(ord.lines.map(l => ({ ...l })));
    setActiveBuilderTab('manual');
    setBuilderModal({ open: true, initialTab: 'manual', editingOrderId: ord.id });
  };

  // Construir renglón(es) desde un mueble PT aplicando las 4 reglas canónicas 4-UP + Polimorfismo PLAN-059
  const buildLinesFromPTItem = (
    pt: PTFurnitureItem | undefined,
    rawCode: string,
    rawDesc: string,
    qty: number,
    color: string,
    variantSuffix = '',
    shouldDecompose = true,
    polyOpts?: {
      precioUnitarioUSD?: number;
      unidadMedida?: string;
      tipoLinea?: OrderLineType;
      rutaLogistica?: LogisticsRouteType;
      bultosDeclarados?: number;
    }
  ): OrderLine360[] => {
    const cleanCode = (pt?.codigo || rawCode || 'PT-STD').trim().toUpperCase();
    const cleanColor = (color || 'ESTÁNDAR').trim().toUpperCase();
    const fullDesc = (rawDesc || pt?.nombre || cleanCode).trim();
    const precioUnit = Math.max(0, Number(polyOpts?.precioUnitarioUSD) || 0);

    // Inferir polimorfismo determinista (P58-D2 & P59-D1/D2)
    let uom = String(polyOpts?.unidadMedida || pt?.unidadMedida || 'UND').trim().toUpperCase();
    let tipoLinea: OrderLineType = polyOpts?.tipoLinea || 'PT_FABRICADO';
    const upperDesc = fullDesc.toUpperCase();

    if (!polyOpts?.tipoLinea) {
      if (
        cleanCode.startsWith('SERV-') ||
        uom === 'SERV' ||
        uom === 'GL' ||
        /\b(FLETE|TRANSPORTE|INSTALACI[OÓ]N|MONTAJE|VI[AÁ]TICO|MANO\s+DE\s+OBRA|SERVICIO)\b/i.test(upperDesc)
      ) {
        tipoLinea = 'SERVICIO_INTANGIBLE';
        uom = 'SERV';
      } else if (
        ['ML', 'METROS', 'MT2', 'M2'].includes(uom) ||
        /\b(POR\s+ML|METRO\s+LINEAL|POR\s+MT2|PARED\s+ACANALADA|PERFIL\s+LED)\b/i.test(upperDesc)
      ) {
        tipoLinea = 'PT_PARAMETRICO';
        if (uom === 'UND' || uom === 'UNID') {
          uom = /\b(MT2|M2|PARED)\b/i.test(upperDesc) ? 'MT2' : 'ML';
        }
      } else if (/\b(SUMINISTRO\s+EXTERNO|FOR[AÁ]NEO|DROPSHIP)\b/i.test(upperDesc)) {
        tipoLinea = 'SUMINISTRO_FORANEO';
      }
    }

    const isService = tipoLinea === 'SERVICIO_INTANGIBLE';
    const isForeign = tipoLinea === 'SUMINISTRO_FORANEO';
    const isParametric =
      tipoLinea === 'PT_PARAMETRICO' || ['ML', 'METROS', 'MT2', 'M2'].includes(uom);

    const rutaLogistica: LogisticsRouteType =
      polyOpts?.rutaLogistica ||
      (isService ? 'NO_APLICA' : isForeign ? 'DIRECTO_OBRA' : 'VIA_GALPON');

    const requiereEmpaqueGalpon = !isService && rutaLogistica !== 'DIRECTO_OBRA';
    const costoUnitBOM = isService || isForeign ? 0 : (pt?.costoTeoricoUSD || 0);

    // Verificar si es un Juego Compuesto 4-UP y debe desglosarse en sub-piezas físicas
    const subKitRaw = pt?.subKitEnsamble || pt?.suggestedSubKit4UP || '';
    const subParts = subKitRaw
      .split(/\r?\n|;/)
      .map(s => s.trim())
      .filter(Boolean);

    if (shouldDecompose && !isService && !isParametric && subParts.length > 1) {
      const subPrecioUnit = Number((precioUnit / subParts.length).toFixed(2));
      return subParts.map((subText, idx) => {
        const partTag = `${idx + 1}/${subParts.length}`;
        const cleanSub = subText.replace(/^(SUB-PIEZA|COMP|PARTE)?\s*\d+\/\d+\s*:?\s*/i, '').trim();
        const codeEtiqueta = variantSuffix
          ? `${cleanCode} [${partTag} ${variantSuffix.toUpperCase()}]`
          : `${cleanCode} [${partTag}]`;
        const descEtiqueta = clientAbbreviate4UP(`${partTag} ${cleanSub}`, 80);
        const subCostBOM = Number((costoUnitBOM / subParts.length).toFixed(2));
        return {
          id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}-${idx}`,
          orderId: orderForm.id || '',
          orderCodigo: orderForm.codigo,
          lineTitle: `${orderForm.codigo} - ${codeEtiqueta}: ${descEtiqueta}`,
          code: cleanCode,
          codeEtiqueta,
          desc: `${fullDesc} — Sub-pieza ${partTag}: ${cleanSub}`,
          descEtiqueta,
          labelLength: descEtiqueta.length,
          labelValid80: descEtiqueta.length <= 80,
          cantidad: qty,
          unidadMedida: uom,
          color: cleanColor,
          precioUnitarioUSD: subPrecioUnit,
          subtotalPrecioUSD: Number((qty * subPrecioUnit).toFixed(2)),
          estadoProduccion: 'Sin producir',
          estadoDespacho: 'Pendiente',
          tipoLinea: 'PT_FABRICADO',
          rutaLogistica: 'VIA_GALPON',
          estadoServicioObra: 'No Aplica',
          requiereEmpaqueGalpon: true,
          bultosDeclarados: Math.max(1, Math.round(qty)),
          bultosCalculados4UP: Math.max(1, Math.round(qty)),
          ptNotionId: pt?.id || null,
          familia: pt?.familia || 'Mobiliario Comercial',
          empaqueNominal: 1,
          esKitCompuesto: true,
          hasBOM: Boolean(pt?.hasBOM),
          costoUnitarioBOMUSD: subCostBOM,
          costoTotalBOMUSD: Number((qty * subCostBOM).toFixed(2)),
          requiereFijacion: Boolean(pt?.requiereFijacion),
          puntosPared: pt?.puntosPared || 0,
          puntosPiso: pt?.puntosPiso || 0
        };
      });
    }

    const codeEtiqueta = variantSuffix
      ? `${cleanCode} [${variantSuffix.trim().toUpperCase()}]`
      : cleanCode;
    const rawLabelText =
      isParametric && !fullDesc.toUpperCase().includes(uom)
        ? `[${qty} ${uom === 'METROS' ? 'ML' : uom}] ${fullDesc}`
        : fullDesc;
    const descEtiqueta = clientAbbreviate4UP(rawLabelText, 80);

    const declaredBultos =
      polyOpts?.bultosDeclarados !== undefined && polyOpts.bultosDeclarados >= 0
        ? polyOpts.bultosDeclarados
        : isService || !requiereEmpaqueGalpon
          ? 0
          : isParametric
            ? 1
            : Math.max(1, Math.ceil(qty / Math.max(1, pt?.empaqueNominal || 1)));

    return [
      {
        id: `temp-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        orderId: orderForm.id || '',
        orderCodigo: orderForm.codigo,
        lineTitle: `${orderForm.codigo} - ${codeEtiqueta}: ${descEtiqueta}`,
        code: cleanCode,
        codeEtiqueta,
        desc: fullDesc,
        descEtiqueta,
        labelLength: descEtiqueta.length,
        labelValid80: descEtiqueta.length <= 80,
        cantidad: qty,
        unidadMedida: uom,
        color: isService ? 'N/A' : cleanColor,
        precioUnitarioUSD: precioUnit,
        subtotalPrecioUSD: Number((qty * precioUnit).toFixed(2)),
        estadoProduccion: 'Sin producir',
        estadoDespacho: 'Pendiente',
        tipoLinea,
        rutaLogistica,
        estadoServicioObra: isService || rutaLogistica === 'DIRECTO_OBRA' ? 'Pendiente' : 'No Aplica',
        requiereEmpaqueGalpon,
        bultosDeclarados: declaredBultos,
        bultosCalculados4UP: declaredBultos,
        ptNotionId: pt?.id || null,
        familia: isService ? 'Servicios / Logística' : pt?.familia || 'General / Accesorios',
        empaqueNominal: isParametric || isService ? 1 : (pt?.empaqueNominal || 1),
        esKitCompuesto: Boolean(pt?.isTrueCompositeKit),
        hasBOM: isService || isForeign ? false : Boolean(pt?.hasBOM),
        costoUnitarioBOMUSD: Number(costoUnitBOM.toFixed(2)),
        costoTotalBOMUSD: Number((qty * costoUnitBOM).toFixed(2)),
        requiereFijacion: isService || isForeign ? false : Boolean(pt?.requiereFijacion),
        puntosPared: isService || isForeign ? 0 : (pt?.puntosPared || 0),
        puntosPiso: isService || isForeign ? 0 : (pt?.puntosPiso || 0)
      }
    ];
  };

  const syncHeaderTotalFromLines = (nextLines: OrderLine360[]) => {
    const sumPrice = Number(
      nextLines.reduce((acc, l) => acc + (Number(l.subtotalPrecioUSD) || 0), 0).toFixed(2)
    );
    if (sumPrice > 0) {
      setOrderForm(prev => ({ ...prev, montoTotalUSD: sumPrice }));
    }
  };

  const handleAddManualPTLine = () => {
    if (!selectedPtCode) return;
    const pt = ptByCode.get(selectedPtCode.toUpperCase());
    const qty = Math.max(0.01, Number(newLineQty) || 1);
    const price = Math.max(0, Number(newLinePriceUSD) || 0);
    const bultosOpt = newLineBultos.trim() !== '' ? Math.max(0, Number(newLineBultos)) : undefined;
    const generated = buildLinesFromPTItem(
      pt,
      pt?.codigo || selectedPtCode,
      pt?.nombre || selectedPtCode,
      qty,
      newLineColor,
      newLineVariantTag,
      autoDecomposeKit,
      {
        precioUnitarioUSD: price,
        unidadMedida: newLineUnit || pt?.unidadMedida || 'UND',
        tipoLinea: newLineType,
        bultosDeclarados: bultosOpt
      }
    );
    setEditableLines(prev => {
      const next = [...prev, ...generated];
      syncHeaderTotalFromLines(next);
      return next;
    });
    setNewLineVariantTag('');
    setNewLineQty('1');
    setNewLinePriceUSD('');
    setNewLineBultos('');
  };

  // Botón rápido para añadir Comodines Canónicos de Servicio (P58-D3 & P59-D2)
  const handleQuickAddServiceWildcard = (
    wildcardCode: 'SERV-FLETE' | 'SERV-INSTALACION' | 'SERV-MANO-OBRA' | 'SERV-MISCELANEO',
    defaultDesc: string
  ) => {
    const pt = ptByCode.get(wildcardCode);
    const price = Math.max(0, Number(newLinePriceUSD) || 0);
    const generated = buildLinesFromPTItem(pt, wildcardCode, defaultDesc, 1, 'N/A', '', false, {
      precioUnitarioUSD: price,
      unidadMedida: 'SERV',
      tipoLinea: 'SERVICIO_INTANGIBLE',
      rutaLogistica: 'NO_APLICA',
      bultosDeclarados: 0
    });
    setEditableLines(prev => {
      const next = [...prev, ...generated];
      syncHeaderTotalFromLines(next);
      return next;
    });
    setNewLinePriceUSD('');
  };

  // Procesar pegado masivo TSV / CSV / JSON en el Importador Híbrido 360° (4 a 8 columnas — P59-D2)
  const handleParseBulkIngestion = () => {
    const raw = bulkRawText.trim();
    if (!raw) return;

    const newLines: OrderLine360[] = [];
    const seenCodeCounts = new Map<string, number>();

    try {
      if (raw.startsWith('[') || raw.startsWith('{')) {
        const parsedJson = JSON.parse(raw);
        const arr = Array.isArray(parsedJson) ? parsedJson : parsedJson.items || [];
        for (const it of arr) {
          const code = String(it.code || it.codigo || 'PT-STD').trim().toUpperCase();
          const pt = ptByCode.get(code) || ptByCode.get(code.replace(/\*+$/, ''));
          const qty = Math.max(0.01, Number(it.cantidad_total || it.cantidad || 1));
          const color = String(it.color || 'NEGRO').trim().toUpperCase();
          const price = Math.max(0, Number(it.precioUnitarioUSD || it.precio || 0));
          const uomOrType = String(it.unidadMedida || it.tipoLinea || '').trim().toUpperCase();
          const bultos =
            it.bultosDeclarados !== undefined ? Math.max(0, Number(it.bultosDeclarados)) : undefined;
          const count = (seenCodeCounts.get(code) || 0) + 1;
          seenCodeCounts.set(code, count);
          const suffix = count > 1 ? `VAR-${count}` : '';
          const built = buildLinesFromPTItem(
            pt,
            code,
            it.desc || it.descripcion || pt?.nombre || code,
            qty,
            color,
            suffix,
            true,
            {
              precioUnitarioUSD: price,
              unidadMedida: ['UND', 'ML', 'METROS', 'MT2', 'JUEGO', 'SERV'].includes(uomOrType)
                ? uomOrType
                : undefined,
              tipoLinea: (ORDER_LINE_TYPES as readonly string[]).includes(uomOrType)
                ? (uomOrType as OrderLineType)
                : undefined,
              bultosDeclarados: bultos
            }
          );
          newLines.push(...built);
        }
      } else {
        // Parsear TSV / CSV línea por línea (4 a 8 cols):
        // CODIGO | DESCRIPCION | CANTIDAD | COLOR | [PRECIO_UNIT_USD] | [UNIDAD_O_TIPO] | [BULTOS] | [RUTA_LOGISTICA]
        const rows = raw.split(/\r?\n/).map(r => r.trim()).filter(Boolean);
        for (const row of rows) {
          const cols = row.includes('\t')
            ? row.split('\t').map(c => c.trim())
            : row.split(/[;|]/).map(c => c.trim());
          if (cols.length === 0) continue;
          const code = String(cols[0] || 'PT-STD').toUpperCase();
          if (code === 'CODIGO' || code === 'CÓDIGO') continue;
          const pt = ptByCode.get(code) || ptByCode.get(code.replace(/\*+$/, ''));
          const desc = cols[1] || pt?.nombre || code;
          const qtyClean = String(cols[2] || '1').replace(',', '.');
          const qty = Math.max(0.01, Number(qtyClean) || 1);
          const color = String(cols[3] || 'NEGRO').toUpperCase();
          const priceClean = String(cols[4] || '0').replace(/[^0-9.-]/g, '');
          const price = Math.max(0, Number(priceClean) || 0);
          const col5 = String(cols[5] || '').trim().toUpperCase();
          const col6Bultos =
            cols[6] !== undefined && cols[6] !== '' ? Math.max(0, Number(cols[6])) : undefined;
          const col7Ruta = String(cols[7] || '').trim().toUpperCase();

          let explicitType: OrderLineType | undefined = undefined;
          let explicitUom: string | undefined = undefined;
          if ((ORDER_LINE_TYPES as readonly string[]).includes(col5)) {
            explicitType = col5 as OrderLineType;
          } else if (['UND', 'UNID', 'ML', 'METROS', 'MT2', 'M2', 'JUEGO', 'GL', 'SERV'].includes(col5)) {
            explicitUom = col5 === 'M2' ? 'MT2' : col5;
            if (col5 === 'SERV' || col5 === 'GL') explicitType = 'SERVICIO_INTANGIBLE';
            else if (['ML', 'METROS', 'MT2', 'M2'].includes(col5)) explicitType = 'PT_PARAMETRICO';
          }

          const count = (seenCodeCounts.get(code) || 0) + 1;
          seenCodeCounts.set(code, count);
          const suffix = count > 1 ? `V${count}` : '';

          const built = buildLinesFromPTItem(pt, code, desc, qty, color, suffix, true, {
            precioUnitarioUSD: price,
            unidadMedida: explicitUom,
            tipoLinea: explicitType,
            bultosDeclarados: col6Bultos,
            rutaLogistica: (LOGISTICS_ROUTE_TYPES as readonly string[]).includes(col7Ruta)
              ? (col7Ruta as LogisticsRouteType)
              : undefined
          });
          newLines.push(...built);
        }
      }

      setEditableLines(newLines);
      syncHeaderTotalFromLines(newLines);
      setActiveBuilderTab('manual');
      notify(
        'success',
        `Ingesta Polimórfica 360° procesada: ${newLines.length} renglones generados con validación 4-UP y cruce BOM.`
      );
    } catch (err: any) {
      notify('error', `Error parseando lote de ingesta: ${err.message}`);
    }
  };

  // Actualizar un campo de una línea editable (incluyendo campos polimórficos PLAN-059)
  const handleUpdateEditableLine = (
    idx: number,
    field:
      | 'codeEtiqueta'
      | 'descEtiqueta'
      | 'cantidad'
      | 'color'
      | 'empaqueNominal'
      | 'precioUnitarioUSD'
      | 'tipoLinea'
      | 'unidadMedida'
      | 'bultosDeclarados'
      | 'rutaLogistica',
    val: any
  ) => {
    setEditableLines(prev => {
      const nextList = prev.map((l, i) => {
        if (i !== idx) return l;
        const next = { ...l };
        if (field === 'cantidad') {
          next.cantidad = Math.max(0.01, Number(val) || 1);
          next.subtotalPrecioUSD = Number((next.cantidad * (next.precioUnitarioUSD || 0)).toFixed(2));
          if (Array.isArray(next.overrideBomLines) && next.overrideBomLines.length > 0) {
            const ovTotal = next.overrideBomLines.reduce(
              (acc, it) =>
                acc +
                (Number(it.cantidadTotalObra) || 1) *
                  (1 + (Number(it.mermaPct) || 0) / 100) *
                  (Number(it.costoUnitarioUSD) || 2.5),
              0
            );
            next.costoTotalBOMUSD = Number(ovTotal.toFixed(2));
            next.costoUnitarioBOMUSD = Number((ovTotal / next.cantidad).toFixed(2));
          } else {
            next.costoTotalBOMUSD = Number((next.cantidad * next.costoUnitarioBOMUSD).toFixed(2));
          }
          if (next.tipoLinea === 'PT_FABRICADO') {
            next.bultosCalculados4UP = Math.max(
              1,
              Math.ceil(next.cantidad / Math.max(1, next.empaqueNominal || 1))
            );
          }
        } else if (field === 'precioUnitarioUSD') {
          next.precioUnitarioUSD = Math.max(0, Number(val) || 0);
          next.subtotalPrecioUSD = Number((next.cantidad * next.precioUnitarioUSD).toFixed(2));
        } else if (field === 'tipoLinea') {
          next.tipoLinea = val as OrderLineType;
          if (val === 'SERVICIO_INTANGIBLE') {
            next.unidadMedida = 'SERV';
            next.rutaLogistica = 'NO_APLICA';
            next.requiereEmpaqueGalpon = false;
            next.bultosDeclarados = 0;
            next.bultosCalculados4UP = 0;
            next.costoUnitarioBOMUSD = 0;
            next.costoTotalBOMUSD = 0;
          } else if (val === 'SUMINISTRO_FORANEO') {
            next.rutaLogistica = 'DIRECTO_OBRA';
            next.requiereEmpaqueGalpon = false;
            next.bultosDeclarados = 0;
            next.bultosCalculados4UP = 0;
            next.costoUnitarioBOMUSD = 0;
            next.costoTotalBOMUSD = 0;
          } else if (val === 'PT_PARAMETRICO') {
            if (!['ML', 'METROS', 'MT2'].includes(String(next.unidadMedida || '').toUpperCase())) {
              next.unidadMedida = 'ML';
            }
            next.rutaLogistica = 'VIA_GALPON';
            next.requiereEmpaqueGalpon = true;
            next.bultosDeclarados = next.bultosDeclarados || 1;
            next.bultosCalculados4UP = next.bultosDeclarados;
          } else {
            next.unidadMedida = 'UND';
            next.rutaLogistica = 'VIA_GALPON';
            next.requiereEmpaqueGalpon = true;
            next.bultosCalculados4UP = Math.max(
              1,
              Math.ceil(next.cantidad / Math.max(1, next.empaqueNominal || 1))
            );
          }
        } else if (field === 'unidadMedida') {
          next.unidadMedida = String(val).toUpperCase();
          if (['ML', 'METROS', 'MT2'].includes(next.unidadMedida)) {
            next.tipoLinea = 'PT_PARAMETRICO';
            next.bultosDeclarados = next.bultosDeclarados || 1;
            next.bultosCalculados4UP = next.bultosDeclarados;
          } else if (next.unidadMedida === 'SERV') {
            next.tipoLinea = 'SERVICIO_INTANGIBLE';
            next.bultosDeclarados = 0;
            next.bultosCalculados4UP = 0;
            next.requiereEmpaqueGalpon = false;
          }
        } else if (field === 'bultosDeclarados') {
          const b = Math.max(0, Math.round(Number(val) || 0));
          next.bultosDeclarados = b;
          next.bultosCalculados4UP = b;
        } else if (field === 'empaqueNominal') {
          next.empaqueNominal = Math.max(1, Number(val) || 1);
          if (next.tipoLinea === 'PT_FABRICADO') {
            next.bultosCalculados4UP = Math.max(
              1,
              Math.ceil(next.cantidad / Math.max(1, next.empaqueNominal))
            );
          }
        } else if (field === 'descEtiqueta') {
          next.descEtiqueta = String(val);
          next.labelLength = next.descEtiqueta.length;
          next.labelValid80 = next.labelLength <= 80;
        } else if (field === 'codeEtiqueta') {
          next.codeEtiqueta = String(val).toUpperCase();
        } else if (field === 'color') {
          next.color = String(val).toUpperCase();
        } else if (field === 'rutaLogistica') {
          next.rutaLogistica = val as LogisticsRouteType;
          if (
            val === 'DIRECTO_OBRA' ||
            val === 'NO_APLICA' ||
            next.tipoLinea === 'SERVICIO_INTANGIBLE'
          ) {
            next.requiereEmpaqueGalpon = false;
            next.bultosCalculados4UP = 0;
          } else {
            next.requiereEmpaqueGalpon = true;
            next.bultosCalculados4UP =
              next.bultosDeclarados && next.bultosDeclarados > 0
                ? next.bultosDeclarados
                : Math.max(1, Math.ceil(next.cantidad / Math.max(1, next.empaqueNominal || 1)));
          }
        }
        return next;
      });
      if (field === 'cantidad' || field === 'precioUnitarioUSD') {
        syncHeaderTotalFromLines(nextList);
      }
      return nextList;
    });
  };

  // Mini-Editor de Receta Override BOM por Renglón (PLAN-059 P59-D3)
  const openOverrideBomEditor = (idx: number) => {
    const ln = editableLines[idx];
    if (!ln) return;
    setOverrideBomModal({
      open: true,
      lineIdx: idx,
      lines: Array.isArray(ln.overrideBomLines) ? ln.overrideBomLines.map(x => ({ ...x })) : [],
      matSearch: '',
      selectedMatCode: '',
      qtyObra: '1',
      mermaPct: '0',
      notas: ''
    });
  };

  const handleClonePTBomIntoOverride = () => {
    if (overrideBomModal.lineIdx === null) return;
    const ln = editableLines[overrideBomModal.lineIdx];
    if (!ln) return;
    const pt = ptByCode.get(ln.code.toUpperCase());
    if (!pt || !Array.isArray(pt.bomLines) || pt.bomLines.length === 0) {
      notify('error', `El modelo ${ln.code} no tiene receta base en el catálogo para clonar.`);
      return;
    }
    const cloned: OverrideBomLine[] = pt.bomLines.map(bl => ({
      matCode: bl.matCode,
      sku: bl.sku || bl.matCode,
      insumoNotionId: bl.insumoNotionId || null,
      nombre: bl.nombre,
      unidad: bl.unidad || 'Unidad',
      cantidadPorUnidad: bl.cantidadBase,
      cantidadTotalObra: Number((bl.cantidadBase * (ln.cantidad || 1)).toFixed(3)),
      mermaPct: bl.mermaPct || 0,
      etapaProductiva: bl.etapaProductiva || 'Carpintería / Estructura',
      costoUnitarioUSD: bl.costoUnitarioUSD || 2.5,
      notas: bl.notas || ''
    }));
    setOverrideBomModal(prev => ({ ...prev, lines: cloned }));
  };

  const handleAddOverrideMaterial = () => {
    const code = overrideBomModal.selectedMatCode.trim().toUpperCase();
    if (!code) return;
    const matRef = materialsCatalog.find(
      m => m.code.toUpperCase() === code || m.sku.toUpperCase() === code
    );
    const qtyTotal = Math.max(0.01, Number(overrideBomModal.qtyObra) || 1);
    const merma = Math.max(0, Number(overrideBomModal.mermaPct) || 0);
    const newItem: OverrideBomLine = {
      matCode: matRef?.code || code,
      sku: matRef?.sku || code,
      insumoNotionId: matRef?.notionId || null,
      nombre: matRef?.nombre || code,
      unidad: matRef?.unidad || 'Unidad',
      cantidadTotalObra: qtyTotal,
      mermaPct: merma,
      costoUnitarioUSD: matRef?.costoUSD || 2.5,
      notas: overrideBomModal.notas.trim()
    };
    setOverrideBomModal(prev => ({
      ...prev,
      lines: [...prev.lines, newItem],
      selectedMatCode: '',
      qtyObra: '1',
      mermaPct: '0',
      notas: ''
    }));
  };

  const handleSaveOverrideBom = () => {
    const idx = overrideBomModal.lineIdx;
    if (idx === null) return;
    const ovLines = overrideBomModal.lines;
    setEditableLines(prev =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        if (ovLines.length === 0) {
          const pt = ptByCode.get(l.code.toUpperCase());
          const baseUnitCost = pt?.costoTeoricoUSD || 0;
          return {
            ...l,
            overrideBomLines: [],
            recetaOverrideJson: '',
            hasBOM: Boolean(pt?.hasBOM),
            costoUnitarioBOMUSD: baseUnitCost,
            costoTotalBOMUSD: Number((l.cantidad * baseUnitCost).toFixed(2))
          };
        }
        const totalOverrideCost = ovLines.reduce((acc, item) => {
          const qtyObra = Number(item.cantidadTotalObra) || 1;
          const mermaFactor = 1 + (Number(item.mermaPct) || 0) / 100;
          const unitCost = Number(item.costoUnitarioUSD) > 0 ? Number(item.costoUnitarioUSD) : 2.5;
          return acc + qtyObra * mermaFactor * unitCost;
        }, 0);
        const unitBomCost =
          l.cantidad > 0 ? Number((totalOverrideCost / l.cantidad).toFixed(2)) : totalOverrideCost;
        return {
          ...l,
          overrideBomLines: ovLines,
          recetaOverrideJson: JSON.stringify(ovLines),
          hasBOM: true,
          costoUnitarioBOMUSD: unitBomCost,
          costoTotalBOMUSD: Number(totalOverrideCost.toFixed(2))
        };
      })
    );
    setOverrideBomModal(prev => ({ ...prev, open: false, lineIdx: null }));
    notify('success', `Receta Override BOM aplicada al renglón (${ovLines.length} insumos).`);
  };

  const handleAbbreviateLine80 = (idx: number) => {
    setEditableLines(prev =>
      prev.map((l, i) => {
        if (i !== idx) return l;
        const abbr = clientAbbreviate4UP(l.descEtiqueta || l.desc, 80);
        return {
          ...l,
          descEtiqueta: abbr,
          labelLength: abbr.length,
          labelValid80: abbr.length <= 80
        };
      })
    );
  };

  const handleDecomposeExistingLine = (idx: number) => {
    const target = editableLines[idx];
    if (!target) return;
    const pt = ptByCode.get(target.code.toUpperCase());
    const expanded = buildLinesFromPTItem(
      pt,
      target.code,
      target.desc,
      target.cantidad,
      target.color,
      '',
      true,
      {
        precioUnitarioUSD: target.precioUnitarioUSD,
        unidadMedida: target.unidadMedida,
        tipoLinea: target.tipoLinea
      }
    );
    setEditableLines(prev => [...prev.slice(0, idx), ...expanded, ...prev.slice(idx + 1)]);
  };

  // Conciliación Presupuesto vs Proyecto (Cross-Check Tripartito PLAN-059 P59-D1)
  const reconciliationSummary = useMemo(() => {
    let discreteUnits = 0;
    let continuousMeasureTotal = 0;
    let continuousLinesCount = 0;
    let servicesAndForeignCount = 0;
    let totalBultos4UP = 0;

    for (const l of editableLines) {
      const t = l.tipoLinea || 'PT_FABRICADO';
      const uom = String(l.unidadMedida || 'UND').toUpperCase();
      const isContinuous = t === 'PT_PARAMETRICO' || ['ML', 'METROS', 'MT2', 'M2'].includes(uom);
      const isNonPhysical =
        t === 'SERVICIO_INTANGIBLE' || t === 'SUMINISTRO_FORANEO' || uom === 'SERV';

      if (isNonPhysical) {
        servicesAndForeignCount++;
      } else if (isContinuous) {
        continuousMeasureTotal = Number((continuousMeasureTotal + l.cantidad).toFixed(2));
        continuousLinesCount++;
        totalBultos4UP += Number(l.bultosCalculados4UP ?? l.bultosDeclarados ?? 1);
      } else {
        discreteUnits += Math.round(l.cantidad);
        totalBultos4UP += Number(
          l.bultosCalculados4UP ??
            Math.max(1, Math.ceil(l.cantidad / Math.max(1, l.empaqueNominal || 1)))
        );
      }
    }

    const totalPhysicalUnits = discreteUnits;
    const totalBOMCost = Number(
      editableLines.reduce((acc, l) => acc + (l.costoTotalBOMUSD || 0), 0).toFixed(2)
    );
    const totalOrderPriceUSD = Number(
      editableLines.reduce((acc, l) => acc + (l.subtotalPrecioUSD || 0), 0).toFixed(2)
    );
    const expected = Number(budgetExpectedUnits) || 0;
    const allValid80 = editableLines.every(l => l.descEtiqueta.length <= 80);

    // Detectar colisiones de codeEtiqueta idénticos
    const codeCounts = new Map<string, number>();
    for (const l of editableLines) {
      const k = l.codeEtiqueta.trim().toUpperCase();
      codeCounts.set(k, (codeCounts.get(k) || 0) + 1);
    }
    const collisions = Array.from(codeCounts.entries())
      .filter(([, c]) => c > 1)
      .map(([k]) => k);

    return {
      totalPhysicalUnits,
      discreteUnits,
      continuousMeasureTotal,
      continuousLinesCount,
      servicesAndForeignCount,
      totalBultos4UP,
      totalBOMCost,
      totalOrderPriceUSD,
      expected,
      hasDiscrepancy:
        expected > 0 &&
        expected !== discreteUnits &&
        expected !== discreteUnits + continuousLinesCount,
      allValid80,
      collisions
    };
  }, [editableLines, budgetExpectedUnits]);

  // Guardar Pedido 360° + Renglones en Notion ERP (BD_Pedidos + BD_Pedidos_Lineas)
  const handleSaveOrder360 = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderForm.codigo.trim()) {
      notify('error', 'Ingresa el Código del Pedido.');
      return;
    }
    if (editableLines.length === 0) {
      notify('error', 'El pedido debe tener al menos 1 renglón de mobiliario PT.');
      return;
    }
    if (!reconciliationSummary.allValid80) {
      notify('error', 'Corrige o abrevia los rótulos térmicos 4-UP que superan los 80 caracteres.');
      return;
    }

    setIsSaving(true);
    try {
      const proj = projects.find(p => p.id === orderForm.proyectoId);
      const cli = clients.find(c => c.id === (orderForm.clienteId || proj?.clienteId));
      const branch = branches.find(b => b.id === (orderForm.sucursalId || proj?.sucursalId));

      const res = await fetch('/api/orders/upsert', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'upsert_order_360',
          orderData: {
            ...orderForm,
            clienteId: cli?.id || orderForm.clienteId || null,
            clienteNombre: cli?.nombre || proj?.clienteNombre || 'Cliente Vinculado',
            clienteRif: cli?.rif || '',
            proyectoId: proj?.id || orderForm.proyectoId || null,
            proyectoNombre: proj?.nombre || 'Proyecto Vinculado',
            sucursalId: branch?.id || orderForm.sucursalId || null,
            sucursalNombre: branch?.nombre || proj?.sucursalNombre || 'Sucursal Principal',
            ciudadEstado: branch?.ciudadEstado || proj?.ciudadEstado || '',
            sustratoPared: branch?.sustratoPared || 'Mampostería / Concreto',
            sustratoPiso: branch?.sustratoPiso || 'Porcelanato / Cerámica'
          },
          lines: editableLines,
          supervisorPIN: supervisorPIN || undefined
        })
      });

      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'Error guardando Pedido 360° en Notion ERP.');
      }

      setBuilderModal({ open: false, initialTab: 'manual', editingOrderId: null });
      notify(
        'success',
        `Pedido "${json.codigo}" sincronizado en Notion ERP con ${json.linesCreated} renglones 4-UP.`
      );
      await fetchOrdersCatalog(true);
      if (onOrderSaved) onOrderSaved();
    } catch (err: any) {
      notify('error', err.message || 'Fallo al sincronizar Pedido 360°.');
    } finally {
      setIsSaving(false);
    }
  };

  // Cambiar estado de una línea en vivo desde el Inspector
  const handleUpdateLineStatusLive = async (
    orderId: string,
    lineId: string,
    field: 'estadoProduccion' | 'estadoDespacho',
    value: string
  ) => {
    try {
      const res = await fetch('/api/orders/upsert', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'update_line_status',
          orderId,
          lineId,
          [field]: value
        })
      });
      const json = await res.json();
      if (!res.ok || !json.ok) {
        throw new Error(json.error || 'Error actualizando estado de línea.');
      }
      setInspectModal(prev => {
        if (!prev.order) return prev;
        return {
          ...prev,
          order: {
            ...prev.order,
            lines: prev.order.lines.map(l => (l.id === lineId ? { ...l, [field]: value } : l))
          }
        };
      });
      notify('success', `Renglón actualizado a "${value}".`);
    } catch (err: any) {
      notify('error', err.message || 'No se pudo actualizar el renglón.');
    }
  };

  return (
    <div className="space-y-3">
      {/* Sub-Header KPI Strip de Pedidos e Ingesta 360° */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Órdenes BD_Pedidos (sale.order)
            </p>
            <p className="text-lg font-black text-white mt-0.5">
              {data?.kpis.totalOrders ?? '—'}
              <span className="text-[11px] font-normal text-teal-400 ml-1.5">
                ({data?.kpis.ordersWithLines ?? 0} con líneas · {data?.kpis.inRampaCount ?? 0} rampa)
              </span>
            </p>
          </div>
          <ClipboardList className="w-6 h-6 text-teal-400/70" />
        </div>

        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Renglones Físicos (BD_Lineas)
            </p>
            <p className="text-lg font-black text-indigo-300 mt-0.5">
              {data?.kpis.totalLines ?? '—'}
              <span className="text-[11px] font-normal text-slate-400 ml-1.5">
                líneas · {data?.kpis.totalUnits ?? 0} und.
              </span>
            </p>
          </div>
          <Layers className="w-6 h-6 text-indigo-400/70" />
        </div>

        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Vínculo PT & Protección 4-UP
            </p>
            <p className="text-lg font-black text-emerald-400 mt-0.5">
              {data?.kpis.linkedToPTLinesCount ?? '—'}
              <span className="text-[11px] font-normal text-emerald-300 ml-1.5">
                enlazados PT · {data?.kpis.protectedLabels80Pct ?? 100}% &le;80ch
              </span>
            </p>
          </div>
          <ShieldCheck className="w-6 h-6 text-emerald-400/70" />
        </div>

        <div className="bg-page border border-borderSubtle rounded-xl p-3 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
              Costo BOM Teórico Acumulado
            </p>
            <p className="text-lg font-black text-amber-300 mt-0.5">
              ${(data?.kpis.totalBOMTheoreticalUSD ?? 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}
              <span className="text-[11px] font-normal text-amber-400 ml-1.5">
                USD (mrp.bom)
              </span>
            </p>
          </div>
          <Package className="w-6 h-6 text-amber-400/70" />
        </div>
      </div>

      {/* Barra de Búsqueda, Filtros y Acciones de Ingesta 360° */}
      <div className="p-3 bg-page border border-borderSubtle rounded-xl flex flex-wrap items-center justify-between gap-2.5">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar por pedido (P0040, PED-TALIB), cliente, tienda o mueble (P484, P1257)..."
            className="w-full pl-9 pr-3 py-1.5 text-xs bg-surface border border-borderSubtle rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus:border-teal-400"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 overflow-x-auto">
            {[
              { id: 'WITH_LINES', label: `Con Líneas 4-UP (${data?.kpis.ordersWithLines ?? 0})` },
              { id: 'RAMPA', label: `🚚 En Rampa (${data?.kpis.inRampaCount ?? 0})` },
              { id: 'PRODUCTION', label: `En Producción (${data?.kpis.activeProductionOrders ?? 0})` },
              { id: 'PRS', label: 'Presupuestos (PRS)' },
              { id: 'ALL', label: `Todos (${orders.length})` }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setModeFilter(tab.id as any)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-semibold transition ${
                  modeFilter === tab.id
                    ? 'bg-teal-500 text-slate-950'
                    : 'bg-surface text-slate-400 hover:text-slate-200 border border-borderSubtle'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <button
            onClick={() => openCreateOrderModal('manual')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-teal-500/20 hover:bg-teal-500/30 text-teal-300 border border-teal-500/40 transition"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>Nuevo Pedido (sale.order)</span>
          </button>

          <button
            onClick={() => openCreateOrderModal('bulk_ingest')}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-400/50 transition"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-indigo-300" />
            <span>Ingesta Híbrida 360° (4-UP)</span>
          </button>

          <button
            onClick={() => fetchOrdersCatalog(true)}
            disabled={loading}
            className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle transition"
            title="Refrescar Pedidos y Renglones desde Notion ERP"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin text-teal-400' : ''}`} />
          </button>
        </div>
      </div>

      {/* Grid de Pedidos 360° */}
      {loading && !data ? (
        <div className="py-14 flex flex-col items-center justify-center gap-3 text-slate-400">
          <Loader2 className="w-7 h-7 animate-spin text-teal-400" />
          <p className="text-xs font-medium">
            Hidratando 634 Órdenes y 4.526 Renglones de Mobiliario con cruce BOM y etiquetas 4-UP...
          </p>
        </div>
      ) : errorMsg ? (
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-200 flex items-center justify-between text-xs">
          <span>{errorMsg}</span>
          <button
            onClick={() => fetchOrdersCatalog(true)}
            className="px-3 py-1 rounded-lg bg-rose-500/20 border border-rose-500/40 font-semibold"
          >
            Reintentar
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filteredOrders.slice(0, visibleLimit).map(ord => (
              <div
                key={ord.id}
                className="bg-page border border-borderSubtle hover:border-teal-500/40 rounded-xl p-4 flex flex-col justify-between gap-3 transition"
              >
                <div className="space-y-2.5">
                  {/* Cabecera de la Orden */}
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 font-mono text-xs font-black border border-teal-500/40">
                          {ord.codigo}
                        </span>
                        <span className="px-1.5 py-0.5 rounded bg-surface text-slate-300 text-[10px] font-bold border border-borderSubtle">
                          {ord.tipoCorto}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-semibold border border-emerald-500/30">
                          {ord.estado}
                        </span>
                        {ord.inRampaActivos && (
                          <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-bold border border-amber-500/40 flex items-center gap-1">
                            <Truck className="w-3 h-3" /> Rampa
                          </span>
                        )}
                      </div>
                      <h4 className="text-xs font-bold text-white mt-1.5 truncate">
                        🏢 {ord.clienteNombre}
                      </h4>
                      <p className="text-[11px] text-slate-400 truncate flex items-center gap-1 mt-0.5">
                        <FolderKanban className="w-3 h-3 text-teal-400 shrink-0" />
                        <span>{ord.proyectoNombre}</span>
                      </p>
                    </div>

                    <button
                      onClick={() => openEditOrderModal(ord)}
                      className="p-1.5 rounded-lg bg-surface hover:bg-surfaceHighest text-slate-300 border border-borderSubtle shrink-0"
                      title="Editar Pedido o Re-Ingestar Renglones 4-UP"
                    >
                      <Edit3 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Sustratos de Sucursal + Resumen Cuantitativo y Costo BOM */}
                  <div className="p-2.5 rounded-lg bg-surface border border-borderSubtle space-y-1.5 text-[11px]">
                    <div className="flex items-center justify-between text-slate-300">
                      <span className="truncate">📍 {ord.sucursalNombre}</span>
                      <span className="font-mono text-[10px] text-slate-400">{ord.fechaDocumento}</span>
                    </div>
                    <div className="flex items-center justify-between pt-1 border-t border-borderSubtle/60">
                      <span className="text-slate-400">
                        <strong className="text-white font-mono">{ord.linesCount}</strong> renglones ·{' '}
                        <strong className="text-teal-300 font-mono">{ord.unidadesTotales}</strong> und. ·{' '}
                        <strong className="text-indigo-300 font-mono">{ord.bultosEstimados}</strong> bultos
                      </span>
                      <span className="font-mono font-bold text-emerald-300">
                        BOM: ${ord.costoTeoricoTotalBOMUSD.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Vista Previa de Renglones 4-UP (primeros 3 renglones) */}
                  {ord.lines.length > 0 ? (
                    <div className="space-y-1">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                        <span>Renglones y Rótulos 4-UP</span>
                        <span
                          className={
                            ord.allLabelsValid80 ? 'text-emerald-400 font-mono' : 'text-amber-400 font-mono'
                          }
                        >
                          {ord.allLabelsValid80 ? '🛡️ 100% ≤80ch' : '⚠️ Revisar >80ch'}
                        </span>
                      </div>
                      {ord.lines.slice(0, 3).map(ln => (
                        <div
                          key={ln.id}
                          className="px-2 py-1 rounded bg-surface/70 border border-borderSubtle flex items-center justify-between gap-2 text-[10px] font-mono"
                        >
                          <div className="min-w-0 truncate">
                            <span className="text-teal-300 font-bold">{ln.codeEtiqueta}</span>{' '}
                            <span className="text-slate-300">{ln.descEtiqueta}</span>
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <span className="px-1.5 py-0.2 rounded bg-page text-white font-bold">
                              x{ln.cantidad}
                            </span>
                            <span
                              className={
                                ln.labelValid80 ? 'text-emerald-400 text-[9px]' : 'text-rose-400 text-[9px]'
                              }
                            >
                              {ln.labelLength}ch
                            </span>
                          </div>
                        </div>
                      ))}
                      {ord.lines.length > 3 && (
                        <p className="text-[10px] text-slate-400 font-mono text-right">
                          +{ord.lines.length - 3} renglones adicionales...
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-[11px] text-amber-300 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 shrink-0" />
                      <span>Sin renglones cargados en BD_Pedidos_Lineas.</span>
                    </div>
                  )}
                </div>

                {/* Botonera Inferior + Puentes de Navegación Cruzada (PLAN-052-R) */}
                <div className="pt-2.5 border-t border-borderSubtle space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <button
                      onClick={() => {
                        setShowRampaJsonExport(false);
                        setInspectModal({ open: true, order: ord });
                      }}
                      className="flex-1 flex items-center justify-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-surface hover:bg-surfaceHighest text-teal-300 border border-teal-500/30 transition"
                    >
                      <Layers className="w-3.5 h-3.5" />
                      <span>Inspeccionar Líneas 4-UP ({ord.linesCount})</span>
                    </button>

                    <button
                      onClick={() => openEditOrderModal(ord)}
                      className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/30 transition"
                    >
                      Ingestar / Editar
                    </button>
                  </div>

                  {(onOpenMRPExplosion || onOpenBOMAuditForOrder || onOpenStoreAllocations) && (
                    <div className="flex items-center justify-between gap-1.5 text-[11px]">
                      {onOpenMRPExplosion && (
                        <button
                          onClick={() =>
                            onOpenMRPExplosion({
                              id: ord.id,
                              codigo: ord.codigo
                            })
                          }
                          className="flex-1 px-2 py-1 rounded bg-emerald-500/15 hover:bg-emerald-500/25 text-emerald-300 border border-emerald-500/40 font-bold transition text-center"
                          title="Abrir Explosión MRP Ex-Ante en 3 Capas para este Pedido"
                        >
                          🧮 Explosión MRP
                        </button>
                      )}
                      {onOpenBOMAuditForOrder && (
                        <button
                          onClick={() =>
                            onOpenBOMAuditForOrder({
                              id: ord.id,
                              codigo: ord.codigo,
                              proyecto: ord.proyectoNombre
                            })
                          }
                          className="flex-1 px-2 py-1 rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 font-semibold transition text-center"
                          title="Abrir Auditoría de Cierre BOM para este Pedido"
                        >
                          ⚖️ Auditoría BOM
                        </button>
                      )}
                      {onOpenStoreAllocations && (
                        <button
                          onClick={() => onOpenStoreAllocations(ord.proyectoNombre)}
                          className="flex-1 px-2 py-1 rounded bg-sky-500/10 hover:bg-sky-500/20 text-sky-300 border border-sky-500/30 font-semibold transition text-center"
                          title="Abrir Asignación de Tiendas para esta Obra"
                        >
                          🏢 Asignar Tienda
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {filteredOrders.length > visibleLimit && (
            <div className="pt-2 flex justify-center">
              <button
                onClick={() => setVisibleLimit(prev => prev + 36)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-surfaceHigh hover:bg-surfaceHighest text-teal-300 border border-teal-500/30 transition"
              >
                Mostrar más pedidos (+36) — Mostrando {visibleLimit} de {filteredOrders.length}
              </button>
            </div>
          )}
        </>
      )}

      {/* =====================================================================
          MODAL 1: INSPECTOR 360° DE PEDIDO, RENGLONES 4-UP Y ESTADOS PLANTA/RAMPA
         ===================================================================== */}
      {inspectModal.open && inspectModal.order && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 overflow-y-auto">
          <div className="bg-surface border border-borderSubtle rounded-2xl w-full max-w-5xl overflow-hidden shadow-2xl my-auto">
            <div className="px-5 py-3.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="px-2 py-0.5 rounded bg-teal-500/20 text-teal-300 font-mono text-xs font-black border border-teal-500/40">
                    {inspectModal.order.codigo}
                  </span>
                  <h3 className="text-sm font-bold text-white">
                    {inspectModal.order.clienteNombre} — {inspectModal.order.proyectoNombre}
                  </h3>
                </div>
                <p className="text-[11px] text-slate-400 mt-0.5">
                  Sucursal: <strong className="text-slate-200">{inspectModal.order.sucursalNombre}</strong> (Pared:{' '}
                  {inspectModal.order.sustratoPared} / Piso: {inspectModal.order.sustratoPiso}) ·{' '}
                  <strong className="text-emerald-400 font-mono">
                    Costo BOM Teórico: ${inspectModal.order.costoTeoricoTotalBOMUSD.toFixed(2)} USD
                  </strong>
                </p>
              </div>
              <button
                onClick={() => setInspectModal({ open: false, order: null })}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 max-h-[78vh] overflow-y-auto text-xs">
              <div className="border border-borderSubtle rounded-xl overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surfaceHigh text-[10px] uppercase text-slate-400 border-b border-borderSubtle">
                      <th className="py-2 px-3">Código Etiqueta 4-UP</th>
                      <th className="py-2 px-3">Rótulo Térmico 4-UP (&le;80 chars)</th>
                      <th className="py-2 px-3">Color</th>
                      <th className="py-2 px-3 text-right">Cant.</th>
                      <th className="py-2 px-3 text-right">Costo BOM</th>
                      <th className="py-2 px-3">Estado Producción</th>
                      <th className="py-2 px-3">Estado Despacho</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderSubtle bg-page">
                    {inspectModal.order.lines.map(ln => (
                      <tr key={ln.id} className="hover:bg-surface/60">
                        <td className="py-2 px-3 font-mono font-bold text-teal-300">
                          {ln.codeEtiqueta}
                          <span className="block text-[10px] text-slate-400 font-normal">
                            {ln.tipoLinea || 'PT_FABRICADO'} · Bultos QR: {ln.bultosCalculados4UP ?? 1}
                          </span>
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-mono text-slate-100">{ln.descEtiqueta}</div>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span
                              className={`text-[10px] font-mono px-1.5 py-0.2 rounded ${
                                ln.labelValid80
                                  ? 'bg-emerald-500/15 text-emerald-300'
                                  : 'bg-rose-500/20 text-rose-300'
                              }`}
                            >
                              {ln.labelLength}/80 chars
                            </span>
                            {ln.ptNotionId && (
                              <span className="text-[10px] text-teal-400 font-mono">✓ Vinculado a PT</span>
                            )}
                            {Array.isArray(ln.overrideBomLines) && ln.overrideBomLines.length > 0 && (
                              <span className="text-[10px] text-amber-300 font-mono font-bold">
                                🧪 Override BOM ({ln.overrideBomLines.length})
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="py-2 px-3 font-mono text-slate-200">{ln.color}</td>
                        <td className="py-2 px-3 text-right font-mono font-bold text-white">
                          {ln.cantidad} <span className="text-[10px] text-cyan-300">{ln.unidadMedida || 'UND'}</span>
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-emerald-300">
                          ${ln.costoTotalBOMUSD.toFixed(2)}
                        </td>
                        <td className="py-2 px-3">
                          <select
                            value={ln.estadoProduccion}
                            onChange={e =>
                              handleUpdateLineStatusLive(
                                inspectModal.order!.id,
                                ln.id,
                                'estadoProduccion',
                                e.target.value
                              )
                            }
                            className="px-2 py-1 rounded bg-surface border border-borderSubtle text-slate-200 text-[11px]"
                          >
                            {ORDER_LINE_STATUSES.map(st => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3">
                          <select
                            value={ln.estadoDespacho}
                            onChange={e =>
                              handleUpdateLineStatusLive(
                                inspectModal.order!.id,
                                ln.id,
                                'estadoDespacho',
                                e.target.value
                              )
                            }
                            className="px-2 py-1 rounded bg-surface border border-borderSubtle text-slate-200 text-[11px]"
                          >
                            {ORDER_DISPATCH_STATUSES.map(st => (
                              <option key={st} value={st}>
                                {st}
                              </option>
                            ))}
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {showRampaJsonExport && (() => {
                const rampaExport = buildClientRampaActivosPayload(inspectModal.order);
                return (
                  <div className="bg-page border border-amber-500/30 rounded-xl p-3 space-y-2">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <span className="text-[11px] font-bold text-amber-300 font-mono block">
                          📋 Bloque Canónico 4-UP para data/pedidos_activos.js (Cortafuegos Rampa QR Activo)
                        </span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          Exportados: <strong className="text-emerald-300">{rampaExport.stats.exportedCount}</strong> renglones físicos ·
                          Servicios filtrados: <strong className="text-fuchsia-300">{rampaExport.stats.excludedServicesCount}</strong> ·
                          Foráneos filtrados: <strong className="text-amber-300">{rampaExport.stats.excludedForeignCount}</strong> ·
                          Paramétricos ML/MT2 consolidados: <strong className="text-cyan-300">{rampaExport.stats.consolidatedParametricCount}</strong>
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard?.writeText(JSON.stringify(rampaExport.payload, null, 2));
                          notify(
                            'success',
                            `Bloque pedidos_activos.js de ${inspectModal.order!.codigo} copiado (${rampaExport.stats.exportedCount} ítems físicos; ${rampaExport.stats.excludedServicesCount + rampaExport.stats.excludedForeignCount} servicios/foráneos filtrados).`
                          );
                        }}
                        className="px-2.5 py-1 rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 text-[11px] font-bold"
                      >
                        Copiar JSON Rampa QR
                      </button>
                    </div>
                    <pre className="text-[10px] font-mono text-slate-300 bg-surface p-2.5 rounded-lg max-h-44 overflow-auto">
                      {JSON.stringify(rampaExport.payload, null, 2)}
                    </pre>
                  </div>
                );
              })()}

              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setShowRampaJsonExport(!showRampaJsonExport)}
                    className="px-3 py-1.5 rounded-xl bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/40 font-semibold text-xs"
                  >
                    📋 {showRampaJsonExport ? 'Ocultar Bloque Rampa QR' : 'Exportar Bloque Rampa QR (pedidos_activos.js)'}
                  </button>
                  {onOpenBOMAuditForOrder && (
                    <button
                      type="button"
                      onClick={() => {
                        const ord = inspectModal.order!;
                        setInspectModal({ open: false, order: null });
                        onOpenBOMAuditForOrder({
                          id: ord.id,
                          codigo: ord.codigo,
                          proyecto: ord.proyectoNombre
                        });
                      }}
                      className="px-3 py-1.5 rounded-xl bg-indigo-500/15 hover:bg-indigo-500/25 text-indigo-300 border border-indigo-500/40 font-semibold text-xs"
                    >
                      ⚖️ Abrir Auditoría BOM
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      const ord = inspectModal.order!;
                      setInspectModal({ open: false, order: null });
                      openEditOrderModal(ord);
                    }}
                    className="px-4 py-2 rounded-xl bg-teal-500/20 text-teal-300 border border-teal-500/40 font-bold"
                  >
                    Editar / Agregar Renglones 4-UP
                  </button>
                  <button
                    type="button"
                    onClick={() => setInspectModal({ open: false, order: null })}
                    className="px-4 py-2 rounded-xl bg-page text-slate-300 border border-borderSubtle"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* =====================================================================
          MODAL 2: CONSTRUCTOR MANUAL ASISTIDO E IMPORTADOR HÍBRIDO 360° (4-UP)
         ===================================================================== */}
      {builderModal.open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm p-3 overflow-y-auto">
          <div className="bg-surface border border-borderSubtle rounded-2xl w-full max-w-6xl overflow-hidden shadow-2xl my-auto">
            <div className="px-5 py-3.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-teal-400" />
                <div>
                  <h3 className="text-sm font-bold text-white">
                    {builderModal.editingOrderId
                      ? `Edición e Ingesta Polimórfica 360° de Pedido — ${orderForm.codigo}`
                      : 'Constructor e Ingesta Polimórfica 360° de Pedidos y Líneas 4-UP (Odoo 18 sale.order)'}
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    Sincronización Dual BD_Pedidos + BD_Pedidos_Lineas · Muebles PT Discretos + Paramétricos ML/MT2 +
                    Servicios SERV-* · Cortafuegos 4-UP (&le;80 chars)
                  </p>
                </div>
              </div>
              <button
                onClick={() => setBuilderModal({ open: false, initialTab: 'manual', editingOrderId: null })}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleSaveOrder360} className="p-5 space-y-4 max-h-[84vh] overflow-y-auto text-xs">
              {/* Bloque 1: Datos Maestros del Pedido (sale.order) */}
              <div className="p-3.5 rounded-xl bg-page border border-borderSubtle grid grid-cols-1 sm:grid-cols-5 gap-3">
                <div>
                  <label className="block text-[11px] text-slate-400 font-semibold mb-1">
                    Código de Pedido / Orden *
                  </label>
                  <input
                    type="text"
                    required
                    value={orderForm.codigo}
                    onChange={e => setOrderForm(p => ({ ...p, codigo: e.target.value.toUpperCase() }))}
                    placeholder="Ej: P0045, PED-AMUCHI-01"
                    className="w-full px-3 py-1.5 rounded-lg bg-surface border border-borderSubtle text-white font-mono font-bold"
                  />
                </div>

                <div>
                  <label className="block text-[11px] text-slate-400 font-semibold mb-1">
                    Tipo de Documento
                  </label>
                  <select
                    value={orderForm.tipoDocumento}
                    onChange={e => setOrderForm(p => ({ ...p, tipoDocumento: e.target.value }))}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-white"
                  >
                    {ORDER_DOC_TYPES.map(dt => (
                      <option key={dt} value={dt}>
                        {dt}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-400 font-semibold mb-1">
                    Estado del Pedido
                  </label>
                  <select
                    value={orderForm.estado}
                    onChange={e => setOrderForm(p => ({ ...p, estado: e.target.value }))}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-white"
                  >
                    {ORDER_STATUSES.map(st => (
                      <option key={st} value={st}>
                        {st}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-slate-400 font-semibold mb-1">
                    Proyecto de Tienda (BD_Proyectos)
                  </label>
                  <select
                    value={orderForm.proyectoId}
                    onChange={e => handleSelectProjectForOrder(e.target.value)}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-surface border border-borderSubtle text-teal-300 font-semibold"
                  >
                    <option value="">-- Seleccionar Proyecto de Obra --</option>
                    {projects.slice(0, 300).map(pr => (
                      <option key={pr.id} value={pr.id}>
                        {pr.nombre} ({pr.clienteNombre})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-[11px] text-emerald-300 font-semibold mb-1">
                    Monto Total Pedido ($ USD)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={orderForm.montoTotalUSD}
                    onChange={e =>
                      setOrderForm(p => ({ ...p, montoTotalUSD: Math.max(0, Number(e.target.value) || 0) }))
                    }
                    placeholder="Auto-calculado o manual"
                    className="w-full px-3 py-1.5 rounded-lg bg-surface border border-emerald-500/40 text-emerald-300 font-mono font-bold"
                  />
                  {reconciliationSummary.totalOrderPriceUSD > 0 && (
                    <div className="flex items-center gap-1 mt-1">
                      <button
                        type="button"
                        onClick={() =>
                          setOrderForm(p => ({
                            ...p,
                            montoTotalUSD: reconciliationSummary.totalOrderPriceUSD
                          }))
                        }
                        className="px-1.5 py-0.5 rounded bg-surface hover:bg-surfaceHighest border border-borderSubtle text-[9px] font-mono text-slate-300"
                        title="Fijar Base Imponible sin IVA (Suma de líneas)"
                      >
                        Base: ${reconciliationSummary.totalOrderPriceUSD.toFixed(2)}
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setOrderForm(p => ({
                            ...p,
                            montoTotalUSD: Number(
                              (reconciliationSummary.totalOrderPriceUSD * 1.16).toFixed(2)
                            )
                          }))
                        }
                        className="px-1.5 py-0.5 rounded bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-500/30 text-[9px] font-mono text-emerald-300 font-bold"
                        title="Fijar Total Facturado con IVA 16%"
                      >
                        +IVA 16%: ${(reconciliationSummary.totalOrderPriceUSD * 1.16).toFixed(2)}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Conmutador entre Modo A (Constructor Manual Asistido) y Modo B (Ingesta Masiva TSV/CSV/JSON) */}
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-borderSubtle pb-2">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setActiveBuilderTab('manual')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      activeBuilderTab === 'manual'
                        ? 'bg-teal-500 text-slate-950'
                        : 'bg-page text-slate-300 border border-borderSubtle'
                    }`}
                  >
                    🛠️ Modo A: Constructor Polimórfico Asistido (PT + ML/MT2 + Comodines SERV-*)
                  </button>
                  <button
                    type="button"
                    onClick={() => setActiveBuilderTab('bulk_ingest')}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                      activeBuilderTab === 'bulk_ingest'
                        ? 'bg-indigo-500 text-white'
                        : 'bg-page text-slate-300 border border-borderSubtle'
                    }`}
                  >
                    📥 Modo B: Ingesta Masiva 360° (TSV 4–8 Cols / JSON + Cross-Check Tripartito)
                  </button>
                </div>

                {/* Cinta de Cross-Check Tripartito (PLAN-059 P59-D1) */}
                <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono">
                  <span className="px-2 py-0.5 rounded bg-teal-500/15 text-teal-300 border border-teal-500/30">
                    Discretos: <strong>{reconciliationSummary.discreteUnits} UND</strong>
                  </span>
                  <span className="px-2 py-0.5 rounded bg-cyan-500/15 text-cyan-300 border border-cyan-500/30">
                    Continuos:{' '}
                    <strong>
                      {reconciliationSummary.continuousMeasureTotal.toFixed(2)} ML/MT2
                    </strong>{' '}
                    ({reconciliationSummary.continuousLinesCount} lín.)
                  </span>
                  <span className="px-2 py-0.5 rounded bg-fuchsia-500/15 text-fuchsia-300 border border-fuchsia-500/30">
                    Servicios/Foráneos: <strong>{reconciliationSummary.servicesAndForeignCount}</strong>
                  </span>
                  <span className="px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/30">
                    Bultos 4-UP: <strong>{reconciliationSummary.totalBultos4UP}</strong>
                  </span>
                  <span className="px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                    BOM: <strong>${reconciliationSummary.totalBOMCost.toFixed(2)}</strong>
                  </span>
                </div>
              </div>

              {activeBuilderTab === 'manual' ? (
                <div className="p-3.5 rounded-xl bg-teal-500/10 border border-teal-500/30 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                    <div className="sm:col-span-4 space-y-1">
                      <div className="flex items-center justify-between">
                        <label className="text-[10px] uppercase text-teal-300 font-bold">
                          Ítem Catálogo PT / Servicio ({filteredPtCatalog.length} disp.)
                        </label>
                        <input
                          type="text"
                          value={ptSearch}
                          onChange={e => setPtSearch(e.target.value)}
                          placeholder="Filtrar P484, SERV-..."
                          className="w-36 px-2 py-0.5 rounded bg-page border border-borderSubtle text-[10px] text-white"
                        />
                      </div>
                      <select
                        value={selectedPtCode}
                        onChange={e => {
                          const code = e.target.value;
                          setSelectedPtCode(code);
                          const pt = ptByCode.get(code.toUpperCase());
                          if (pt) {
                            const u = String(pt.unidadMedida || pt.unidad || 'UND').toUpperCase();
                            if (u.includes('ML') || u.includes('METRO')) setNewLineUnit('ML');
                            else if (u.includes('MT2') || u.includes('M2')) setNewLineUnit('MT2');
                            else if (u === 'SERV' || u === 'GL' || code.startsWith('SERV-')) setNewLineUnit('SERV');
                            else setNewLineUnit('UND');
                          }
                        }}
                        className="w-full px-2.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-white"
                      >
                        <option value="">-- Seleccionar Mueble PT o Comodín --</option>
                        {filteredPtCatalog.slice(0, 400).map(pt => (
                          <option key={pt.id} value={pt.codigo}>
                            {pt.codigo} — {pt.nombre.slice(0, 52)} [{pt.unidadMedida || pt.unidad || 'UND'}]{' '}
                            {pt.isTrueCompositeKit ? '🧩 [JUEGO 4-UP]' : ''} (${pt.costoTeoricoUSD} BOM)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="sm:col-span-1">
                      <label className="block text-[10px] uppercase text-slate-300 font-semibold mb-1">
                        Cant./Med.
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0.01"
                        value={newLineQty}
                        onChange={e => setNewLineQty(e.target.value)}
                        className="w-full px-2 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                      />
                    </div>

                    <div className="sm:col-span-1">
                      <label className="block text-[10px] uppercase text-cyan-300 font-semibold mb-1">
                        Unidad
                      </label>
                      <select
                        value={newLineUnit}
                        onChange={e => setNewLineUnit(e.target.value)}
                        className="w-full px-1.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-cyan-300 font-mono font-bold"
                      >
                        <option value="UND">UND</option>
                        <option value="ML">ML</option>
                        <option value="MT2">MT2</option>
                        <option value="JUEGO">JUEGO</option>
                        <option value="SERV">SERV</option>
                      </select>
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] uppercase text-emerald-300 font-semibold mb-1">
                        Precio Unit. ($)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={newLinePriceUSD}
                        onChange={e => setNewLinePriceUSD(e.target.value)}
                        placeholder="0.00"
                        className="w-full px-2 py-1.5 rounded-lg bg-page border border-borderSubtle text-emerald-300 font-mono"
                      />
                    </div>

                    <div className="sm:col-span-1">
                      <label className="block text-[10px] uppercase text-amber-300 font-semibold mb-1">
                        Bultos 4-UP
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={newLineBultos}
                        onChange={e => setNewLineBultos(e.target.value)}
                        placeholder="Auto"
                        className="w-full px-2 py-1.5 rounded-lg bg-page border border-borderSubtle text-amber-300 font-mono"
                      />
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-[10px] uppercase text-slate-300 font-semibold mb-1">
                        Color / Sufijo
                      </label>
                      <div className="flex gap-1">
                        <input
                          type="text"
                          value={newLineColor}
                          onChange={e => setNewLineColor(e.target.value.toUpperCase())}
                          placeholder="COLOR"
                          className="w-2/3 px-2 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                        />
                        <input
                          type="text"
                          value={newLineVariantTag}
                          onChange={e => setNewLineVariantTag(e.target.value.toUpperCase())}
                          placeholder="VAR"
                          className="w-1/3 px-1.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                        />
                      </div>
                    </div>

                    <div className="sm:col-span-1">
                      <button
                        type="button"
                        onClick={handleAddManualPTLine}
                        disabled={!selectedPtCode}
                        className="w-full py-1.5 px-2 rounded-lg bg-teal-500 hover:bg-teal-400 text-slate-950 font-black disabled:opacity-40"
                      >
                        + Añadir
                      </button>
                    </div>
                  </div>

                  {/* Botones rápidos de Comodines de Servicios (PLAN-059 P59-D2) y Regla 4-UP */}
                  <div className="flex flex-wrap items-center justify-between gap-2 text-[11px] text-slate-300 pt-1 border-t border-teal-500/20">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={autoDecomposeKit}
                        onChange={e => setAutoDecomposeKit(e.target.checked)}
                      />
                      <span>
                        🧩 <strong>Regla 1 Empaque 4-UP:</strong> Desglosar Juegos Compuestos en sub-piezas (
                        <code className="text-indigo-300">empaque_nominal = 1</code>)
                      </span>
                    </label>

                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] uppercase font-bold text-fuchsia-300">
                        ⚡ Comodines Servicios (0 Bultos QR):
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          handleQuickAddServiceWildcard(
                            'SERV-FLETE',
                            'SERVICIO DE TRANSPORTE Y FLETE DEDICADO A TIENDA'
                          )
                        }
                        className="px-2 py-0.5 rounded bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-500/40 text-[10px] font-mono font-bold"
                      >
                        + SERV-FLETE
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleQuickAddServiceWildcard(
                            'SERV-INSTALACION',
                            'SERVICIO DE INSTALACION Y MONTAJE EN TIENDA'
                          )
                        }
                        className="px-2 py-0.5 rounded bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-500/40 text-[10px] font-mono font-bold"
                      >
                        + SERV-INSTALACION
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleQuickAddServiceWildcard(
                            'SERV-MANO-OBRA',
                            'MANO DE OBRA ESPECIALIZADA Y ADECUACION EN SITIO'
                          )
                        }
                        className="px-2 py-0.5 rounded bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-500/40 text-[10px] font-mono font-bold"
                      >
                        + SERV-MANO-OBRA
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          handleQuickAddServiceWildcard(
                            'SERV-MISCELANEO',
                            'SUMINISTRO E INSTALACION MISCELANEA EN OBRA'
                          )
                        }
                        className="px-2 py-0.5 rounded bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-200 border border-fuchsia-500/40 text-[10px] font-mono font-bold"
                      >
                        + SERV-MISCELANEO
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-3.5 rounded-xl bg-indigo-500/10 border border-indigo-500/30 space-y-3">
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <span className="text-[11px] font-bold uppercase text-indigo-300">
                      Pegado Masivo 4 a 8 Columnas (CODIGO | DESC | CANT | COLOR | [PRECIO_USD] | [UNIDAD/TIPO] | [BULTOS] | [RUTA]) o JSON:
                    </span>
                    <div className="flex items-center gap-2">
                      <label className="text-[11px] text-slate-300">
                        Total Piezas Discretas Presupuesto (Cross-Check UND):
                      </label>
                      <input
                        type="number"
                        value={budgetExpectedUnits}
                        onChange={e => setBudgetExpectedUnits(e.target.value)}
                        placeholder="Ej: 15"
                        className="w-20 px-2 py-1 rounded bg-page border border-borderSubtle text-white font-mono"
                      />
                    </div>
                  </div>

                  <textarea
                    rows={4}
                    value={bulkRawText}
                    onChange={e => setBulkRawText(e.target.value)}
                    placeholder={`P484\tMODULO DE CAJA 76X75X70 CON AUXILIAR\t1\tBLANCO\t850\tUND\t1\tVIA_GALPON\nP-REV-WALL\tREVESTIMIENTO WALL PANEL MADERA\t146\tROBLE\t29.50\tML\t1\tVIA_GALPON\nSERV-FLETE\tSERVICIO DE TRANSPORTE A VALENCIA\t1\tN/A\t600\tSERV\t0\tNO_APLICA`}
                    className="w-full px-3 py-2 rounded-lg bg-page border border-borderSubtle text-white font-mono text-xs"
                  />

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleParseBulkIngestion}
                      className="px-4 py-1.5 rounded-lg bg-indigo-500 hover:bg-indigo-400 text-white font-bold flex items-center gap-1.5"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      <span>Procesar Lote Polimórfico (4–8 Cols), Cruzar Catálogo PT y Cortafuegos 4-UP</span>
                    </button>
                  </div>
                </div>
              )}

              {/* Alerta de Conciliación Presupuesto vs Proyecto o Colisiones */}
              {(reconciliationSummary.hasDiscrepancy || reconciliationSummary.collisions.length > 0) && (
                <div className="p-3 rounded-xl bg-amber-500/15 border border-amber-500/40 text-amber-200 text-[11px] flex flex-wrap items-center justify-between gap-2">
                  <div>
                    {reconciliationSummary.hasDiscrepancy && (
                      <p>
                        ⚠️ <strong>Cross-Check Tripartito (Unidades Discretas vs. Presupuesto):</strong> El presupuesto
                        declara <strong>{reconciliationSummary.expected} UND</strong> y la tabla suma{' '}
                        <strong>{reconciliationSummary.discreteUnits} UND discretas</strong> (excluyendo{' '}
                        {reconciliationSummary.continuousMeasureTotal.toFixed(2)} ML/MT2 continuos y{' '}
                        {reconciliationSummary.servicesAndForeignCount} servicios/foráneos).
                      </p>
                    )}
                    {reconciliationSummary.collisions.length > 0 && (
                      <p>
                        ⚠️ <strong>Colisión de Código Etiqueta:</strong> Agrega sufijo a{' '}
                        {reconciliationSummary.collisions.join(', ')}.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Tabla Editable Polimórfica de Renglones 4-UP */}
              <div className="border border-borderSubtle rounded-xl overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surfaceHigh text-[10px] uppercase text-slate-400 border-b border-borderSubtle">
                      <th className="py-2 px-2">Cód. Etiqueta 4-UP</th>
                      <th className="py-2 px-2">Rótulo Térmico 4-UP (Máx. 80 chars)</th>
                      <th className="py-2 px-1.5">Tipo / UoM</th>
                      <th className="py-2 px-1.5">Color</th>
                      <th className="py-2 px-1.5 text-right">Cant./Med.</th>
                      <th className="py-2 px-1.5 text-right">Precio Unit.</th>
                      <th className="py-2 px-1.5 text-right">Bultos QR</th>
                      <th className="py-2 px-2 text-right">Costo BOM</th>
                      <th className="py-2 px-2 text-center">Acciones 4-UP / BOM</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderSubtle bg-page">
                    {editableLines.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="py-8 text-center text-slate-500 italic">
                          Sin renglones aún. Añade un mueble PT o servicio arriba, o procesa un lote en Modo B.
                        </td>
                      </tr>
                    ) : (
                      editableLines.map((ln, idx) => {
                        const hasOverride =
                          Array.isArray(ln.overrideBomLines) && ln.overrideBomLines.length > 0;
                        return (
                          <tr key={ln.id} className="hover:bg-surface/60">
                            <td className="py-1.5 px-2">
                              <input
                                type="text"
                                value={ln.codeEtiqueta}
                                onChange={e => handleUpdateEditableLine(idx, 'codeEtiqueta', e.target.value)}
                                className="w-28 px-2 py-1 rounded bg-surface border border-borderSubtle text-teal-300 font-mono font-bold text-[11px]"
                              />
                            </td>
                            <td className="py-1.5 px-2">
                              <div className="flex items-center gap-1">
                                <input
                                  type="text"
                                  value={ln.descEtiqueta}
                                  onChange={e => handleUpdateEditableLine(idx, 'descEtiqueta', e.target.value)}
                                  className={`flex-1 min-w-[180px] px-2 py-1 rounded bg-surface border font-mono text-[11px] ${
                                    ln.descEtiqueta.length <= 80
                                      ? 'border-borderSubtle text-white'
                                      : 'border-rose-500 text-rose-300'
                                  }`}
                                />
                                <span
                                  className={`px-1 py-0.5 rounded font-mono text-[9px] shrink-0 ${
                                    ln.descEtiqueta.length <= 80
                                      ? 'bg-emerald-500/15 text-emerald-300'
                                      : 'bg-rose-500/20 text-rose-300 font-bold'
                                  }`}
                                >
                                  {ln.descEtiqueta.length}/80
                                </span>
                              </div>
                            </td>
                            <td className="py-1.5 px-1.5">
                              <div className="flex items-center gap-1">
                                <select
                                  value={ln.tipoLinea || 'PT_FABRICADO'}
                                  onChange={e => handleUpdateEditableLine(idx, 'tipoLinea', e.target.value)}
                                  className="px-1.5 py-1 rounded bg-surface border border-borderSubtle text-[10px] font-mono text-slate-200"
                                >
                                  {ORDER_LINE_TYPES.map(t => (
                                    <option key={t} value={t}>
                                      {t === 'PT_FABRICADO'
                                        ? '📦 PT Discreto'
                                        : t === 'PT_PARAMETRICO'
                                        ? '📐 Paramétrico'
                                        : t === 'SUMINISTRO_FORANEO'
                                        ? '🚚 Foráneo'
                                        : '⚡ Servicio'}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  value={ln.unidadMedida || 'UND'}
                                  onChange={e => handleUpdateEditableLine(idx, 'unidadMedida', e.target.value)}
                                  className="px-1 py-1 rounded bg-surface border border-borderSubtle text-[10px] font-mono text-cyan-300"
                                >
                                  <option value="UND">UND</option>
                                  <option value="ML">ML</option>
                                  <option value="MT2">MT2</option>
                                  <option value="JUEGO">JGO</option>
                                  <option value="SERV">SERV</option>
                                </select>
                                <select
                                  value={
                                    ln.rutaLogistica ||
                                    (ln.tipoLinea === 'SERVICIO_INTANGIBLE'
                                      ? 'NO_APLICA'
                                      : ln.tipoLinea === 'SUMINISTRO_FORANEO'
                                      ? 'DIRECTO_OBRA'
                                      : 'VIA_GALPON')
                                  }
                                  onChange={e => handleUpdateEditableLine(idx, 'rutaLogistica', e.target.value)}
                                  className="px-1 py-1 rounded bg-surface border border-borderSubtle text-[10px] font-mono text-amber-200"
                                  title="Ruta Logística (Vía Galpón genera rótulo 4-UP; Directo Obra omite Rampa QR)"
                                >
                                  <option value="VIA_GALPON">🏭 Galpón</option>
                                  <option value="DIRECTO_OBRA">🚚 Sitio</option>
                                  <option value="NO_APLICA">⚪ N/A</option>
                                </select>
                              </div>
                            </td>
                            <td className="py-1.5 px-1.5">
                              <input
                                type="text"
                                value={ln.color}
                                onChange={e => handleUpdateEditableLine(idx, 'color', e.target.value)}
                                className="w-20 px-1.5 py-1 rounded bg-surface border border-borderSubtle text-white font-mono text-[11px]"
                              />
                            </td>
                            <td className="py-1.5 px-1.5 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={ln.cantidad}
                                onChange={e => handleUpdateEditableLine(idx, 'cantidad', e.target.value)}
                                className="w-16 px-1.5 py-1 rounded bg-surface border border-borderSubtle text-right text-white font-mono text-[11px]"
                              />
                            </td>
                            <td className="py-1.5 px-1.5 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={ln.precioUnitarioUSD || 0}
                                onChange={e => handleUpdateEditableLine(idx, 'precioUnitarioUSD', e.target.value)}
                                className="w-20 px-1.5 py-1 rounded bg-surface border border-borderSubtle text-right text-emerald-300 font-mono text-[11px]"
                              />
                            </td>
                            <td className="py-1.5 px-1.5 text-right">
                              {ln.tipoLinea === 'SERVICIO_INTANGIBLE' ||
                              ln.requiereEmpaqueGalpon === false ||
                              (ln.tipoLinea === 'SUMINISTRO_FORANEO' && ln.rutaLogistica !== 'VIA_GALPON') ? (
                                <span className="px-1.5 py-0.5 rounded bg-slate-800 text-slate-500 font-mono text-[10px]">
                                  0 QR
                                </span>
                              ) : (
                                <input
                                  type="number"
                                  min="0"
                                  value={
                                    ln.bultosDeclarados !== null && ln.bultosDeclarados !== undefined
                                      ? ln.bultosDeclarados
                                      : ln.bultosCalculados4UP ?? 1
                                  }
                                  onChange={e => handleUpdateEditableLine(idx, 'bultosDeclarados', e.target.value)}
                                  className="w-14 px-1.5 py-1 rounded bg-surface border border-borderSubtle text-right text-amber-300 font-mono text-[11px]"
                                  title="Bultos físicos 4-UP para Rampa QR"
                                />
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono">
                              <div className="text-emerald-300 text-[11px]">
                                ${ln.costoTotalBOMUSD.toFixed(2)}
                              </div>
                              {hasOverride && (
                                <span className="text-[9px] text-amber-300 font-bold block">
                                  🧪 Override ({ln.overrideBomLines!.length})
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <div className="flex items-center justify-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => openOverrideBomEditor(idx)}
                                  className={`px-1.5 py-1 rounded text-[10px] font-mono font-bold border ${
                                    hasOverride
                                      ? 'bg-amber-500/25 text-amber-200 border-amber-500/50'
                                      : 'bg-surface hover:bg-surfaceHighest text-cyan-300 border-borderSubtle'
                                  }`}
                                  title="Mini-Editor Receta Override BOM por Renglón (PLAN-059 P59-D3)"
                                >
                                  🧪 BOM
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleAbbreviateLine80(idx)}
                                  className="p-1 rounded bg-surface hover:bg-surfaceHighest text-amber-300"
                                  title="Abreviar rótulo térmico a <=80 caracteres"
                                >
                                  <Scissors className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDecomposeExistingLine(idx)}
                                  className="p-1 rounded bg-surface hover:bg-surfaceHighest text-indigo-300"
                                  title="Desglosar Kit Compuesto en sub-piezas 4-UP"
                                >
                                  <Layers className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() =>
                                    setEditableLines(prev => {
                                      const next = prev.filter((_, i) => i !== idx);
                                      syncHeaderTotalFromLines(next);
                                      return next;
                                    })
                                  }
                                  className="p-1 rounded bg-surface hover:bg-surfaceHighest text-rose-400"
                                  title="Eliminar renglón"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pie de Guardado Transaccional */}
              <div className="pt-3 border-t border-borderSubtle flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] text-slate-400">PIN Supervisor:</span>
                  <input
                    type="password"
                    value={supervisorPIN}
                    onChange={e => setSupervisorPIN(e.target.value)}
                    placeholder="Opcional si JWT"
                    className="w-28 px-2.5 py-1 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      setBuilderModal({ open: false, initialTab: 'manual', editingOrderId: null })
                    }
                    className="px-4 py-2 rounded-xl bg-page text-slate-300 border border-borderSubtle"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={isSaving || !reconciliationSummary.allValid80}
                    className="flex items-center gap-2 px-5 py-2 rounded-xl font-bold bg-teal-500 hover:bg-teal-400 text-slate-950 shadow-lg transition disabled:opacity-50"
                  >
                    {isSaving ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4" />
                    )}
                    <span>
                      Sincronizar Pedido + {editableLines.length} Renglones 4-UP en Notion ERP
                    </span>
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* =====================================================================
          MODAL 3: MINI-EDITOR DE RECETA OVERRIDE BOM POR RENGLÓN (PLAN-059 P59-D3)
         ===================================================================== */}
      {overrideBomModal.open && overrideBomModal.lineIdx !== null && editableLines[overrideBomModal.lineIdx] && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
          <div className="bg-surface border border-amber-500/40 rounded-2xl w-full max-w-3xl overflow-hidden shadow-2xl">
            <div className="px-5 py-3.5 bg-surfaceHigh border-b border-borderSubtle flex items-center justify-between">
              <div>
                <h3 className="text-sm font-bold text-amber-300 flex items-center gap-2">
                  <span>🧪 Receta Override BOM Específica de Obra (Sin Mutar Catálogo Maestro)</span>
                </h3>
                <p className="text-[11px] text-slate-400 font-mono">
                  Renglón: {editableLines[overrideBomModal.lineIdx].codeEtiqueta} —{' '}
                  {editableLines[overrideBomModal.lineIdx].descEtiqueta} (Cant./Med.:{' '}
                  {editableLines[overrideBomModal.lineIdx].cantidad}{' '}
                  {editableLines[overrideBomModal.lineIdx].unidadMedida || 'UND'})
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOverrideBomModal(prev => ({ ...prev, open: false, lineIdx: null }))}
                className="text-slate-400 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="p-5 space-y-4 text-xs max-h-[78vh] overflow-y-auto">
              <div className="flex flex-wrap items-center justify-between gap-2 bg-page p-3 rounded-xl border border-borderSubtle">
                <div className="text-[11px] text-slate-300">
                  Puedes <strong>clonar la receta base del PT</strong> y ajustar cantidades totales de obra, o agregar
                  insumos reales de <code className="text-teal-300">BD_Catalogo_Insumos</code> para este renglón.
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleClonePTBomIntoOverride}
                    className="px-3 py-1.5 rounded-lg bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-200 border border-indigo-500/40 font-bold text-[11px]"
                  >
                    📋 Clonar Receta Base del PT
                  </button>
                  {overrideBomModal.lines.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setOverrideBomModal(prev => ({ ...prev, lines: [] }))}
                      className="px-2.5 py-1.5 rounded-lg bg-rose-500/15 hover:bg-rose-500/25 text-rose-300 border border-rose-500/30 font-semibold text-[11px]"
                    >
                      Limpiar Override
                    </button>
                  )}
                </div>
              </div>

              {/* Buscador e Inyector de Insumo */}
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end bg-amber-500/10 border border-amber-500/30 p-3 rounded-xl">
                <div className="sm:col-span-6 space-y-1">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase font-bold text-amber-300">
                      Insumo de Almacén ({materialsCatalog.length} disp.)
                    </label>
                    <input
                      type="text"
                      value={overrideBomModal.matSearch}
                      onChange={e => setOverrideBomModal(prev => ({ ...prev, matSearch: e.target.value }))}
                      placeholder="Filtrar MDF, TUBO, PINTURA..."
                      className="w-36 px-2 py-0.5 rounded bg-page border border-borderSubtle text-[10px] text-white"
                    />
                  </div>
                  <select
                    value={overrideBomModal.selectedMatCode}
                    onChange={e => setOverrideBomModal(prev => ({ ...prev, selectedMatCode: e.target.value }))}
                    className="w-full px-2.5 py-1.5 rounded-lg bg-page border border-borderSubtle text-white"
                  >
                    <option value="">-- Seleccionar Insumo para Override --</option>
                    {materialsCatalog
                      .filter(m => {
                        const q = overrideBomModal.matSearch.trim().toLowerCase();
                        if (!q) return true;
                        return (
                          m.code.toLowerCase().includes(q) ||
                          m.nombre.toLowerCase().includes(q) ||
                          m.sku.toLowerCase().includes(q)
                        );
                      })
                      .slice(0, 250)
                      .map(m => (
                        <option key={m.code} value={m.code}>
                          {m.code} — {m.nombre.slice(0, 48)} ({m.unidad} · Stock: {m.stockBase ?? 0} · ${m.costoUSD})
                        </option>
                      ))}
                  </select>
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase text-slate-300 font-semibold mb-1">
                    Cant. Total Obra
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={overrideBomModal.qtyObra}
                    onChange={e => setOverrideBomModal(prev => ({ ...prev, qtyObra: e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] uppercase text-slate-300 font-semibold mb-1">
                    Merma (%)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    min="0"
                    value={overrideBomModal.mermaPct}
                    onChange={e => setOverrideBomModal(prev => ({ ...prev, mermaPct: e.target.value }))}
                    className="w-full px-2 py-1.5 rounded-lg bg-page border border-borderSubtle text-white font-mono"
                  />
                </div>

                <div className="sm:col-span-2">
                  <button
                    type="button"
                    onClick={handleAddOverrideMaterial}
                    disabled={!overrideBomModal.selectedMatCode}
                    className="w-full py-1.5 px-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-slate-950 font-black disabled:opacity-40"
                  >
                    + Agregar
                  </button>
                </div>
              </div>

              {/* Tabla de líneas del Override BOM */}
              <div className="border border-borderSubtle rounded-xl overflow-hidden">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-surfaceHigh text-[10px] uppercase text-slate-400 border-b border-borderSubtle">
                      <th className="py-2 px-2.5">Código</th>
                      <th className="py-2 px-2.5">Insumo</th>
                      <th className="py-2 px-2 text-right">Cant. Total Obra</th>
                      <th className="py-2 px-2 text-right">Merma %</th>
                      <th className="py-2 px-2 text-right">Costo Unit.</th>
                      <th className="py-2 px-2.5 text-right">Subtotal BOM</th>
                      <th className="py-2 px-2 text-center">Quitar</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-borderSubtle bg-page">
                    {overrideBomModal.lines.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-center text-slate-500 italic">
                          Sin override activo (usará la receta estándar del Catálogo Maestro PT).
                        </td>
                      </tr>
                    ) : (
                      overrideBomModal.lines.map((ov, oIdx) => {
                        const sub =
                          (Number(ov.cantidadTotalObra) || 0) *
                          (1 + (Number(ov.mermaPct) || 0) / 100) *
                          (Number(ov.costoUnitarioUSD) || 2.5);
                        return (
                          <tr key={`${ov.matCode}-${oIdx}`}>
                            <td className="py-1.5 px-2.5 font-mono font-bold text-amber-300">{ov.matCode}</td>
                            <td className="py-1.5 px-2.5 text-white">{ov.nombre}</td>
                            <td className="py-1.5 px-2 text-right">
                              <input
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={ov.cantidadTotalObra}
                                onChange={e => {
                                  const v = Math.max(0.01, Number(e.target.value) || 0.01);
                                  setOverrideBomModal(prev => ({
                                    ...prev,
                                    lines: prev.lines.map((item, k) =>
                                      k === oIdx ? { ...item, cantidadTotalObra: v } : item
                                    )
                                  }));
                                }}
                                className="w-20 px-1.5 py-0.5 rounded bg-surface border border-borderSubtle text-right text-white font-mono"
                              />
                            </td>
                            <td className="py-1.5 px-2 text-right font-mono text-slate-300">{ov.mermaPct || 0}%</td>
                            <td className="py-1.5 px-2 text-right font-mono text-slate-300">
                              ${(Number(ov.costoUnitarioUSD) || 2.5).toFixed(2)}
                            </td>
                            <td className="py-1.5 px-2.5 text-right font-mono text-emerald-300 font-bold">
                              ${sub.toFixed(2)}
                            </td>
                            <td className="py-1.5 px-2 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  setOverrideBomModal(prev => ({
                                    ...prev,
                                    lines: prev.lines.filter((_, k) => k !== oIdx)
                                  }))
                                }
                                className="p-1 rounded bg-surface hover:bg-surfaceHighest text-rose-400"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-borderSubtle">
                <div className="text-[11px] font-mono text-slate-300">
                  Costo Total Override BOM:{' '}
                  <strong className="text-emerald-400">
                    $
                    {overrideBomModal.lines
                      .reduce(
                        (acc, it) =>
                          acc +
                          (Number(it.cantidadTotalObra) || 0) *
                            (1 + (Number(it.mermaPct) || 0) / 100) *
                            (Number(it.costoUnitarioUSD) || 2.5),
                        0
                      )
                      .toFixed(2)}{' '}
                    USD
                  </strong>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setOverrideBomModal(prev => ({ ...prev, open: false, lineIdx: null }))}
                    className="px-4 py-1.5 rounded-xl bg-page text-slate-300 border border-borderSubtle"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveOverrideBom}
                    className="px-4 py-1.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold"
                  >
                    Guardar Override BOM en Renglón
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
